import React, { useState, useEffect, useRef, useCallback } from "react";
import Navigation from "./Navigation";
import { t as tr, locale } from "../i18n";
import { ItemPreview } from "./Shop";
import Coin from "./Coin";
import { confirmDialog } from "../dialog";
import AdminInventory from "./AdminInventory";
import { navigate, ROUTES } from "../router";
import BackArrow from "./BackArrow";
import AdminBatchAdd from "./AdminBatchAdd";
import AdminLyrics from "./AdminLyrics";
const API = process.env.REACT_APP_API_URL || "";

const EMPTY_FORM = {
  short_title: "", question: "", answers: ["", "", "", ""],
  correct_answer: 0, difficulty: "", theme_id: "", type: "", media_url: "",
  format: "classic",
};

// Question formats (for the "classic" quiz types)
const QUESTION_FORMATS = [
  { id: "classic",   name: tr("admin.formatClassic"),           desc: tr("admin.formatClassicDesc") },
  { id: "petit_bac", name: tr("admin.formatPetitBac"),           desc: tr("admin.formatPetitBacDesc") },
  { id: "chrono",    name: tr("admin.formatChrono"), desc: tr("admin.formatChronoDesc") },
];

// Default categories of a scattergories question
const PETIT_BAC_DEFAULTS = ["Animal", "Célébrité", "Monument", "Métier", "Fruit / Légume", "Objet", "Sport"];
const EMPTY_USER = { username: "", email: "", role: "user", password: "", question_limit: "" };
const ROLES = ["superadmin", "admin", "user", "anonymous"];
const ROLE_LABELS = { superadmin: tr("admin.roleSuperadmin"), admin: "Admin", user: tr("admin.roleUser"), anonymous: tr("admin.roleAnonymous") };
const ROLE_COLORS = { superadmin: "#f8cf01", admin: "#f58700", user: "#4ade80", anonymous: "#94a3b8" };

// Hardcoded quiz types — mirrors backend QUIZ_TYPES constant
const QUIZ_TYPES = [
  { id: 1, name: "Culture",       contentType: "classic",   answerType: "libre",    adminAccess: true },
  { id: 2, name: "Blind Test",    contentType: "audio",     answerType: "both",     adminAccess: true },
  { id: 3, name: "Buzzer / Battle Royale", contentType: "classic", answerType: "multiple", adminAccess: true },
  // La banque tient dans le champ `question` : un mot par entrée, rien d'autre.
  { id: 4, name: "Pictionnary",   contentType: "words",     answerType: "none",     adminAccess: true },
  // Bombe (id 5) is auto-generated (syllables from the dictionary): nothing to manage,
  // so it is intentionally absent from the admin.
  // Battle Royale (id 6) is a virtual type: it shares the Buzzer bank and never
  // appears as its own admin category, so it is intentionally absent here.
  // Références (id 7) est virtuel lui aussi : il joue sur la banque d'images
  // ci-dessous, donc pas de catégorie propre non plus.
  // `aliases` : la réponse est vérifiée par la machine, pas par l'hôte — on y
  // saisit donc aussi les variantes acceptées (« GTA » pour « Grand Theft Auto »).
  { id: 8, name: "Pixel Guess / Références", contentType: "image", answerType: "libre", adminAccess: true, aliases: true, pixelated: true },
  // Smash or Pass : une image, un titre, un sous-titre facultatif — aucune
  // bonne réponse à saisir, c'est le joueur qui tranche.
  { id: 9, name: "Smash or Pass", contentType: "cards", answerType: "none", adminAccess: true },
  // Hémicycle : les deux extrêmes tiennent dans `question`, « Froid / Chaud ».
  { id: 10, name: "Hémicycle", contentType: "pairs", answerType: "none", adminAccess: true },
  // Undercover : deux mots voisins, même stockage ; lequel revient aux civils
  // se tire en partie. `pairLabels` : les libellés des deux champs.
  { id: 11, name: "Undercover", contentType: "pairs", answerType: "none", adminAccess: true, pairLabels: ["admin.pairWord1", "admin.pairWord2"] },
];

function fmt(isoStr) {
  if (!isoStr) return "—";
  // SQLite écrit l'UTC sans fuseau (« 2026-09-29 10:00:00 ») : lu tel quel, le
  // navigateur le prendrait pour l'heure locale.
  const s = /^\d{4}-\d\d-\d\d \d\d:\d\d(:\d\d)?$/.test(isoStr) ? `${isoStr.replace(" ", "T")}Z` : isoStr;
  return new Date(s).toLocaleString(locale(), { dateStyle: "short", timeStyle: "short" });
}
function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
function initials(name) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

// Aperçu pixellisé d'une image (mode Pixel Guess) : la pixellisation du jeu se
// fait côté serveur, on la reproduit ici via un canvas à basse résolution
// agrandi en nearest-neighbor, avec un curseur du plus flou (palier 0) au net.
function PixelPreview({ src, levels = 8 }) {
  const canvasRef = useRef(null);
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const aspect = img.naturalHeight / img.naturalWidth || 1;
      // Résolution interne : ~14 px de large au plus flou → ~360 px au net
      const minRes = 14, maxRes = 360;
      const res = Math.round(minRes * Math.pow(maxRes / minRes, levels < 2 ? 1 : level / (levels - 1)));
      canvas.width = res;
      canvas.height = Math.max(1, Math.round(res * aspect));
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = src;
  }, [src, level, levels]);
  return (
    <div className="adm-test-pixel">
      <canvas ref={canvasRef} className="adm-test-pixel-canvas" />
      <div className="adm-test-pixel-ctrl">
        <span>Flou</span>
        <input type="range" min="0" max={levels - 1} value={level}
          onChange={e => setLevel(Number(e.target.value))} />
        <span>Net</span>
      </div>
    </div>
  );
}

// `section` decide de la moitie du panneau que l'on ouvre :
//   "library" -> la questiotheque (types, themes, questions) ;
//   "system"  -> l'administration (tableau de bord, comptes, reglages serveur).
// Les deux partagent le meme composant, mais jamais la meme colonne.
export default function Admin({ section = "library", onBack, account, onAuthenticated, onSessionExpired, navProps = {} }) {
  // ── Auth ──────────────────────────────────────────────────────────────────
  // The session is the shared player account: one token for admin and player, so
  // logging out anywhere logs out everywhere. Admin access is gated on the role.
  const token = account?.token || "";
  const isAdmin = !!token && ["admin", "superadmin"].includes(account?.role);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // La création du superadmin ne passe plus par ici : elle est le tout premier
  // écran du jeu (voir Setup.js), donc l'admin n'est jamais atteint sans compte.

  // ── Navigation ────────────────────────────────────────────────────────────
  const isSystemSection = section === "system";
  const [view, setView] = useState(isSystemSection ? "dashboard" : "library");
  const [selectedQuizTypeId, setSelectedQuizTypeId] = useState(null);
  const [selectedThemeId,    setSelectedThemeId]    = useState(null);

  // ── Content data ──────────────────────────────────────────────────────────
  const [quizTypes, setQuizTypes] = useState([]);
  const [themes,    setThemes]    = useState([]);
  const [questions, setQuestions] = useState([]);

  // ── Editor ────────────────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);

  // ── System data ───────────────────────────────────────────────────────────
  const [dashboard, setDashboard]   = useState(null);
  const [users,     setUsers]       = useState([]);
  const [settings,  setSettings]    = useState({});
  const [backups,     setBackups]     = useState([]);
  // Texte affiché pendant un import / une restauration (opérations bloquantes)
  const [backupBusy,  setBackupBusy]  = useState("");
  // Menu mobile : sous 900px la colonne de gauche devient un panneau plein écran
  // Le menu de gauche : ouvert d'office sur grand écran (colonne fixe), fermé
  // sur téléphone (tiroir). Choisir une entrée ne le referme que sur téléphone.
  const isMobileNav = () => window.matchMedia("(max-width: 900px)").matches;
  const [navOpen, setNavOpen] = useState(() => !isMobileNav());
  const closeNavOnMobile = () => { if (isMobileNav()) setNavOpen(false); };
  const importRef = useRef(null);
  const [logs,        setLogs]        = useState([]);
  const [logFilter,   setLogFilter]   = useState("all");
  const [adminGames,  setAdminGames]  = useState([]);
  const [apiKeys,     setApiKeys]     = useState([]);
  const [reports,       setReports]       = useState([]);
  const [invUserId,     setInvUserId]     = useState(null);   // inventaire ouvert (compte)
  const [batchOpen,     setBatchOpen]     = useState(false);  // ajout en lot dans le thème ouvert
  const [reportsClosed, setReportsClosed] = useState(false);  // voir les incidents supprimés / résolus
  const [reportSearch,  setReportSearch]  = useState("");
  const [reportShot,    setReportShot]    = useState(null);   // { id, url } — capture en popup
  const [reportDetail,  setReportDetail]  = useState(null);   // signalement affiché en détail
  const [apiKeyName,  setApiKeyName]  = useState("");
  const [newApiKey,   setNewApiKey]   = useState(null);   // { name, key } — montrée une seule fois
  const [gameSettings, setGameSettings] = useState(null);   // { schema, values }
  const [advancedGroup, setAdvancedGroup] = useState(null);     // onglet
  const [advancedSection, setAdvancedSection] = useState(null); // mini-filtre dans l'onglet
  const [gameForm, setGameForm] = useState({});
  const [settingsForm, setSettingsForm] = useState({
    domain: "", port: "3000", public_url: "", maintenance: "false",
  });

  // ── UI ────────────────────────────────────────────────────────────────────
  const [msg,           setMsg]          = useState({ text: "", error: false });
  const [createOpen,    setCreateOpen]   = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [searchQuery,   setSearchQuery]  = useState("");
  const [selectedIds,   setSelectedIds]  = useState(new Set()); // multi-selection of questions

  // ── Question test (player preview) ─────────────────────────────────────────
  const [testMode,      setTestMode]     = useState(false); // is the modal open?
  const [testAnswer,    setTestAnswer]   = useState("");    // free-text input
  const [testChoice,    setTestChoice]   = useState(null);  // chosen index (multiple choice)
  const [testRevealed,  setTestRevealed] = useState(false);
  const [testTimer,     setTestTimer]    = useState(20);
  const [testOrder,     setTestOrder]    = useState([]);   // chrono: current order of the images
  const [testPb,        setTestPb]       = useState([]);   // scattergories: one input per category
  const testDragIdx = useRef(null);
  const createRef  = useRef(null);
  const fileInputRef = useRef(null);
  const chronoSlotRef = useRef(null); // chrono slot (0-3) targeted by the next upload, null = main media

  // ── Modals ────────────────────────────────────────────────────────────────
  const [showThemeModal,    setShowThemeModal]    = useState(false);
  const [newThemeName,      setNewThemeName]      = useState("");
  const [newThemeQtId,      setNewThemeQtId]      = useState("");

  const [renameThemeModal,  setRenameThemeModal]  = useState(null); // { id, name }

  // Move / copy modal
  // { kind: "theme"|"question", action: "move"|"copy", sourceId, targetQtId, targetThemeId, newName }
  const [transferModal, setTransferModal] = useState(null);
  const [showUserModal,     setShowUserModal]      = useState(false);
  const [editingUser,       setEditingUser]        = useState(null);
  const [userForm,          setUserForm]           = useState(EMPTY_USER);
  const [userRoleFilter,    setUserRoleFilter]     = useState("all");
  const [passwordResets,    setPasswordResets]     = useState([]);
  // SSO : la configuration lue du serveur, et le formulaire en cours d'édition.
  // Le secret n'est jamais relu — le champ reste vide tant qu'on n'en saisit pas un.
  const [sso,     setSso]     = useState(null);
  const [ssoForm, setSsoForm] = useState({ enabled: false, issuer: "", clientId: "", clientSecret: "", label: "" });

  // ── Boutique : avatars, bannières, rayons ─────────────────────────────────
  // shopAdmin : { items, sections, kinds, currencies, rarities } lus du serveur.
  // shopModal : null = fermée, sinon le formulaire { id?, kind, name, currency, rarity, image }.
  const [shopAdmin,  setShopAdmin]  = useState(null);
  const [shopKind,   setShopKind]   = useState("avatar");
  const [shopModal,  setShopModal]  = useState(null);
  const [shopUploading, setShopUploading] = useState(false);

  // ── Blind Test import (yt-dlp) ─────────────────────────────────────────────
  // btImport: null = closed, otherwise { themeId, url, phase, tracks[], error, jobId, progress, ... }
  // phases: "input" -> "loading" -> "review" -> "importing" -> "done"
  const [btImport,       setBtImport]       = useState(null);
  const [ytdlp,          setYtdlp]          = useState(null);  // { installed, version } | { installed:false, error }
  const [ytdlpUpdating,  setYtdlpUpdating]  = useState(false);
  const btPollRef = useRef(null);
  useEffect(() => () => clearInterval(btPollRef.current), []);

  // L'admin suit le thème du reste du jeu : plus de mode sombre imposé.

  // Clears the multi-selection when the context changes (theme, type, view)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedThemeId, selectedQuizTypeId, view]);

  // Countdown of the test modal
  useEffect(() => {
    if (!testMode || testRevealed || testTimer <= 0) return;
    const iv = setInterval(() => setTestTimer(t => {
      if (t <= 1) { setTestRevealed(true); return 0; }
      return t - 1;
    }), 1000);
    return () => clearInterval(iv);
  }, [testMode, testRevealed, testTimer]);

  function openTest() {
    const resolvedType = form.type || deriveTypeFromTheme(form.theme_id);
    if (form.format === "chrono") {
      const imgs = form.answers.filter(a => a && a.trim());
      if (imgs.length !== 4) { flash(tr("admin.testUpload4Images"), true); return; }
      setTestOrder([...imgs].sort(() => 0.5 - Math.random()));
    } else if (form.format === "petit_bac") {
      if (!form.question.trim()) { flash(tr("admin.testFillPrompt"), true); return; }
      const cats = form.answers.filter(c => c && c.trim());
      if (cats.length < 1) { flash(tr("admin.testAddCategory"), true); return; }
      setTestPb(cats.map(() => ""));
    } else {
      if (!form.question.trim() && !form.media_url) { flash(tr("admin.testNothing"), true); return; }
      if (form.format === "classic" && resolvedType === "multiple" && form.answers.filter(a => a.trim()).length < 2) {
        flash(tr("admin.testFillTwoAnswers"), true); return;
      }
    }
    setTestAnswer(""); setTestChoice(null); setTestRevealed(false); setTestTimer(20);
    setTestMode(true);
  }

  // Close dropdown on outside click
  useEffect(() => {
    if (!createOpen) return;
    const h = (e) => { if (createRef.current && !createRef.current.contains(e.target)) setCreateOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [createOpen]);

  useEffect(() => {
    if (isAdmin) { fetchContent(); }
  // eslint-disable-next-line
  }, [isAdmin]);

  // Entree par l'administration : la premiere page a besoin de ses donnees,
  // que seul `goTo` va chercher d'habitude.
  useEffect(() => {
    if (isAdmin && isSystemSection) goTo("dashboard");
  // eslint-disable-next-line
  }, [isAdmin, isSystemSection]);

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const authHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Invalid / expired token -> clear the shared session (falls back to login)
  const authFetch = useCallback(async (url, opts = {}) => {
    const r = await fetch(url, { ...opts, headers: { ...opts.headers, ...{ Authorization: `Bearer ${token}` } } }).catch(() => null);
    if (r?.status === 401) { onSessionExpired && onSessionExpired(); return null; }
    return r;
  }, [token, onSessionExpired]);

  async function fetchContent() {
    await Promise.all([fetchQuizTypes(), fetchThemes(), fetchQuestions()]);
  }
  async function fetchQuizTypes() {
    try { const r = await authFetch(`${API}/admin/quiz-types`); if (r?.ok) setQuizTypes(await r.json()); } catch (_) {}
  }
  async function fetchThemes() {
    try { const r = await authFetch(`${API}/admin/themes`); if (r?.ok) setThemes(await r.json()); } catch (_) {}
  }
  async function fetchQuestions() {
    try { const r = await authFetch(`${API}/admin/questions`); if (r?.ok) setQuestions(await r.json()); } catch (_) {}
  }
  async function fetchDashboard() {
    try { const r = await authFetch(`${API}/admin/dashboard`); if (r?.ok) setDashboard(await r.json()); } catch (_) {}
  }
  async function fetchUsers() {
    try { const r = await authFetch(`${API}/admin/users`); if (r?.ok) setUsers(await r.json()); } catch (_) {}
  }
  async function fetchSso() {
    try {
      const r = await authFetch(`${API}/admin/sso`);
      if (!r?.ok) return;
      const d = await r.json();
      setSso(d);
      setSsoForm({ enabled: d.enabled, issuer: d.issuer, clientId: d.clientId, clientSecret: "", label: d.label });
    } catch (_) {}
  }
  async function saveSso() {
    const r = await authFetch(`${API}/admin/sso`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ssoForm),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.error"), true);
    await fetchSso();
    flash(tr("admin.saved"));
  }
  async function testSso() {
    const r = await authFetch(`${API}/admin/sso/test`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ issuer: ssoForm.issuer }),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.error"), true);
    flash(tr("admin.ssoTestOk"));
  }
  async function fetchPasswordResets() {
    try { const r = await authFetch(`${API}/admin/password-resets`); if (r?.ok) setPasswordResets(await r.json()); } catch (_) {}
  }
  async function dismissPasswordReset(id) {
    const r = await authFetch(`${API}/admin/password-resets/${id}`, { method: "DELETE" });
    // La pastille du menu est comptée par le tableau de bord : elle se relit ici,
    // sinon elle continue d'annoncer une demande qui n'existe plus.
    if (r?.ok) { setPasswordResets(prev => prev.filter(p => p.id !== id)); fetchDashboard(); }
  }
  async function fetchGameSettings() {
    try {
      const r = await authFetch(`${API}/admin/game-settings`);
      if (r?.ok) { const d = await r.json(); setGameSettings(d); setGameForm(d.values); }
    } catch (_) {}
  }
  async function saveGameSettings() {
    const r = await authFetch(`${API}/admin/game-settings`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(gameForm),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.error"), true);
    setGameForm(d.values); flash(tr("admin.gameSettingsSaved"));
  }
  async function resetGameSettings() {
    if (!await confirmDialog(tr("admin.confirmResetSettings"))) return;
    const r = await authFetch(`${API}/admin/game-settings/reset`, { method: "POST" });
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.error"), true);
    setGameForm(d.values); flash(tr("admin.gameSettingsReset"));
  }

  async function fetchSettings() {
    try {
      const r = await authFetch(`${API}/admin/settings`);
      if (r?.ok) { const s = await r.json(); setSettings(s); setSettingsForm(f => ({ ...f, ...s })); }
    } catch (_) {}
  }
  async function fetchBackups() {
    try { const r = await authFetch(`${API}/admin/backups`); if (r?.ok) setBackups(await r.json()); } catch (_) {}
  }
  async function fetchLogs(level = logFilter) {
    try {
      const r = await authFetch(`${API}/admin/logs?level=${level}`);
      if (r?.ok) setLogs(await r.json());
    } catch (_) {}
  }
  async function fetchAdminGames() {
    try { const r = await authFetch(`${API}/admin/games`); if (r?.ok) setAdminGames(await r.json()); } catch (_) {}
  }
  // Verrouiller un thème : les admins ne peuvent plus y ajouter de questions.
  async function toggleThemeLock(theme) {
    const r = await authFetch(`${API}/admin/themes/${theme.id}/lock`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ locked: !theme.locked }),
    });
    if (!r?.ok) return flash(tr("admin.error"), true);
    setThemes(prev => prev.map(t => (t.id === theme.id ? { ...t, locked: theme.locked ? 0 : 1 } : t)));
    flash(theme.locked ? tr("admin.themeUnlockedMsg", { name: theme.name }) : tr("admin.themeLockedMsg", { name: theme.name }));
  }
  async function fetchReports(closed = reportsClosed) {
    try { const r = await authFetch(`${API}/admin/reports${closed ? "?closed=1" : ""}`); if (r?.ok) setReports(await r.json()); } catch (_) {}
  }
  // La capture passe par l'API authentifiée : elle n'est pas dans le dossier public.
  async function openReportShot(rep) {
    const r = await authFetch(`${API}/admin/reports/${rep.id}/screenshot`);
    if (!r?.ok) return flash(tr("admin.reportNoShot"), true);
    setReportShot({ id: rep.id, url: URL.createObjectURL(await r.blob()) });
  }
  function closeReportShot() {
    if (reportShot) URL.revokeObjectURL(reportShot.url);
    setReportShot(null);
  }
  async function resolveReport(rep) {
    if (!await confirmDialog(tr("admin.reportConfirmResolve", { id: rep.id }))) return;
    const r = await authFetch(`${API}/admin/reports/${rep.id}/resolve`, { method: "POST" });
    const d = await r?.json().catch(() => ({}));
    if (r?.ok) { fetchReports(); flash(tr("admin.reportResolved", { id: rep.id, mail: d.mail })); }
    else flash(d?.error || tr("admin.error"), true);
  }
  async function deleteReport(rep) {
    if (!await confirmDialog(tr("admin.reportConfirmDelete", { id: rep.id }))) return;
    const r = await authFetch(`${API}/admin/reports/${rep.id}`, { method: "DELETE" });
    if (r?.ok) { fetchReports(); flash(tr("admin.reportDeleted", { id: rep.id })); }
    else flash(tr("admin.error"), true);
  }
  async function fetchApiKeys() {
    try { const r = await authFetch(`${API}/admin/api-keys`); if (r?.ok) setApiKeys(await r.json()); } catch (_) {}
  }
  async function createApiKey() {
    const name = apiKeyName.trim();
    if (!name) return;
    const r = await authFetch(`${API}/admin/api-keys`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }),
    });
    if (r?.ok) { setNewApiKey(await r.json()); setApiKeyName(""); fetchApiKeys(); }
    else flash(tr("admin.error"), true);
  }
  async function revokeApiKey(k) {
    if (!await confirmDialog(tr("admin.apiKeyConfirmRevoke", { name: k.name }))) return;
    const r = await authFetch(`${API}/admin/api-keys/${k.id}`, { method: "DELETE" });
    if (r?.ok) { fetchApiKeys(); flash(tr("admin.apiKeyRevoked", { name: k.name })); }
    else flash(tr("admin.error"), true);
  }
  async function forceStopGame(id) {
    if (!await confirmDialog(tr("admin.confirmStopGame", { code: id }))) return;
    const r = await authFetch(`${API}/admin/games/${id}`, { method: "DELETE" });
    if (r?.ok) { await fetchAdminGames(); flash(tr("admin.gameStopped", { code: id })); }
    else flash(tr("admin.error"), true);
  }

  async function fetchShopAdmin() {
    try { const r = await authFetch(`${API}/admin/shop`); if (r?.ok) setShopAdmin(await r.json()); } catch (_) {}
  }
  async function toggleShopSection(sec) {
    const r = await authFetch(`${API}/admin/shop/sections/${sec.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ comingSoon: !sec.comingSoon }),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    if (!r.ok) return flash((await safeJson(r)).error || tr("admin.error"), true);
    fetchShopAdmin();
  }
  async function uploadShopImage(file) {
    if (!file) return;
    setShopUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    const r = await authFetch(`${API}/admin/shop/upload`, { method: "POST", body: fd });
    setShopUploading(false);
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok || !d.url) return flash(d.error || tr("admin.error"), true);
    setShopModal(m => (m ? { ...m, image: d.url } : m));
  }
  async function saveShopItem() {
    const m = shopModal;
    const body = { name: m.name, currency: m.currency, rarity: m.rarity, image: m.image || undefined };
    const r = await authFetch(m.id ? `${API}/admin/shop/items/${m.id}` : `${API}/admin/shop/items`, {
      method: m.id ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(m.id ? body : { ...body, kind: m.kind }),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.error"), true);
    setShopModal(null);
    fetchShopAdmin();
    flash(tr("admin.saved"));
  }
  async function deleteShopItem(item) {
    if (!await confirmDialog(tr("admin.shopConfirmDelete", { name: item.name }))) return;
    const r = await authFetch(`${API}/admin/shop/items/${item.id}`, { method: "DELETE" });
    if (!r) return flash(tr("admin.networkError"), true);
    if (!r.ok) return flash((await safeJson(r)).error || tr("admin.error"), true);
    fetchShopAdmin();
  }

  function flash(text, error = false) {
    setMsg({ text, error });
    setTimeout(() => setMsg({ text: "", error: false }), 3000);
  }

  function goTo(v) {
    setView(v);
    closeNavOnMobile();
    if (v === "dashboard") fetchDashboard();
    else if (v === "users")   { fetchUsers(); if (isSuperAdmin) fetchPasswordResets(); }
    else if (v === "network") fetchSettings();
    else if (v === "sso")     fetchSso();
    else if (v === "advanced") fetchGameSettings();
    else if (v === "backups") fetchBackups();
    else if (v === "logs")    fetchLogs("all");
    else if (v === "games")   fetchAdminGames();
    else if (v === "apikeys") { setNewApiKey(null); fetchApiKeys(); }
    else if (v === "reports") { setReportsClosed(false); setReportSearch(""); fetchReports(false); }
    else if (v === "shop")    fetchShopAdmin();
  }

  // ── Auth ──────────────────────────────────────────────────────────────────
  async function login() {
    setLoginError("");
    try {
      const r = await fetch(`${API}/admin/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim() || undefined, password }),
      });
      const d = await safeJson(r);
      if (!r.ok) return setLoginError(d.error || tr("admin.badCredentials"));
      onAuthenticated({ token: d.token, username: d.username, role: d.role });
    } catch (_) { setLoginError(tr("admin.networkError")); }
  }
  function logout() {
    if (onSessionExpired) onSessionExpired(); // clears the shared account (player + admin)
    if (onBack) onBack();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  async function safeJson(r) {
    try { return await r.json(); } catch (_) { return {}; }
  }

  // ── Media upload ───────────────────────────────────────────────────────────
  // slot === null -> the question's main media; slot 0-3 -> chrono image no. slot+1
  async function uploadMedia(file, slot = null) {
    if (!file) return;
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/admin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) { flash(d.error || tr("admin.uploadError"), true); return; }
      if (slot === null) setForm(f => ({ ...f, media_url: d.url }));
      else setForm(f => {
        const a = [...f.answers];
        while (a.length < 4) a.push("");
        a[slot] = d.url;
        return { ...f, answers: a };
      });
    } catch (_) { flash(tr("admin.uploadNetworkError"), true); }
    finally { setUploadingMedia(false); }
  }

  // ── Blind Test import via yt-dlp ───────────────────────────────────────────
  function openBtImport(themeId) {
    setBtImport({ themeId, url: "", phase: "input", tracks: [], error: "", jobId: null, progress: null });
    fetchYtdlp();
  }
  function closeBtImport() {
    clearInterval(btPollRef.current);
    setBtImport(null);
  }
  async function fetchYtdlp() {
    try { const r = await authFetch(`${API}/admin/blindtest/ytdlp`); if (r?.ok) setYtdlp(await r.json()); } catch (_) {}
  }
  async function updateYtdlp() {
    setYtdlpUpdating(true);
    try {
      const r = await authFetch(`${API}/admin/blindtest/ytdlp/update`, { method: "POST" });
      const d = await safeJson(r);
      if (r?.ok) { setYtdlp({ installed: true, version: d.version }); flash(tr("admin.ytdlpUpdated", { version: d.version })); }
      else flash(d.error || tr("admin.updateFailed"), true);
    } catch (_) { flash(tr("admin.networkError"), true); }
    finally { setYtdlpUpdating(false); }
  }
  async function btPreview() {
    const url = (btImport?.url || "").trim();
    if (!url) return;
    setBtImport(s => ({ ...s, phase: "loading", error: "" }));
    const r = await authFetch(`${API}/admin/blindtest/preview`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url }),
    });
    if (!r) return setBtImport(s => ({ ...s, phase: "input", error: tr("admin.networkError") }));
    const d = await safeJson(r);
    if (!r.ok) return setBtImport(s => ({ ...s, phase: "input", error: d.error || tr("admin.previewFailed") }));
    setBtImport(s => ({
      ...s, phase: "review", playlistTitle: d.playlistTitle, truncated: d.truncated,
      importMax: d.importMax || 30,
      // Ce qui existe déjà en banque part décoché ; l'administrateur peut le
      // recocher si le doublon signalé n'en est pas un.
      tracks: (d.tracks || []).map(t => ({ ...t, selected: !t.duplicate })),
    }));
  }
  function btToggleTrack(i) {
    setBtImport(s => ({ ...s, tracks: s.tracks.map((t, j) => j === i ? { ...t, selected: !t.selected } : t) }));
  }
  function btEditTrack(i, field, value) {
    setBtImport(s => ({ ...s, tracks: s.tracks.map((t, j) => j === i ? { ...t, [field]: value } : t) }));
  }
  function btSelectAll(val) {
    setBtImport(s => ({ ...s, tracks: s.tracks.map(t => ({ ...t, selected: val })) }));
  }
  async function btStartImport() {
    const chosen = btImport.tracks.filter(t => t.selected && t.title.trim());
    if (chosen.length === 0) return flash(tr("admin.importPickOne"), true);
    if (chosen.length > btImport.importMax) return flash(tr("admin.importMaxN", { n: btImport.importMax }), true);
    const r = await authFetch(`${API}/admin/blindtest/import`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        theme_id: btImport.themeId,
        tracks: chosen.map(t => ({ id: t.id, search: t.search, source: t.source, title: t.title.trim(), artist: (t.artist || "").trim() })),
      }),
    });
    const d = await safeJson(r);
    if (!r?.ok) return flash(d.error || tr("admin.importFailed"), true);
    setBtImport(s => ({ ...s, phase: "importing", jobId: d.jobId, progress: { status: "running", total: d.total, done: 0, current: null, created: [], errors: [] } }));
    clearInterval(btPollRef.current);
    btPollRef.current = setInterval(async () => {
      const pr = await authFetch(`${API}/admin/blindtest/import/${d.jobId}`);
      if (!pr?.ok) return;
      const p = await pr.json();
      if (p.status === "done" || p.status === "error") {
        clearInterval(btPollRef.current);
        setBtImport(s => s ? ({ ...s, phase: "done", progress: p }) : s);
        await fetchQuestions();
      } else {
        setBtImport(s => s ? ({ ...s, progress: p }) : s);
      }
    }, 1500);
  }

  // ── Themes ────────────────────────────────────────────────────────────────
  async function createTheme() {
    if (!newThemeName.trim() || !newThemeQtId) { flash(tr("admin.themePickTypeAndName"), true); return; }
    const r = await authFetch(`${API}/admin/themes`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newThemeName.trim(), quiz_type_id: parseInt(newThemeQtId) }),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    if (!r.ok) { const d = await safeJson(r); return flash(d.error || tr("admin.createError"), true); }
    await fetchThemes(); setNewThemeName(""); setNewThemeQtId(""); setShowThemeModal(false); flash(tr("admin.themeCreated"));
  }

  async function deleteTheme(th) {
    const count = questions.filter(q => q.theme_id === th.id).length;
    const warning = count > 0
      ? tr("admin.confirmDeleteThemeWithQuestions", { name: th.name, count })
      : tr("admin.confirmDeleteTheme", { name: th.name });
    if (!await confirmDialog(warning)) return;
    const r = await authFetch(`${API}/admin/themes/${th.id}`, { method: "DELETE" });
    if (!r) return flash(tr("admin.networkError"), true);
    if (!r.ok) { const d = await safeJson(r); return flash(d.error || tr("admin.deleteError"), true); }
    if (selectedThemeId === th.id) setSelectedThemeId(null);
    await Promise.all([fetchThemes(), fetchQuestions()]);
    flash(tr("admin.themeDeleted"));
  }

  async function renameTheme() {
    if (!renameThemeModal) return;
    const name = renameThemeModal.name.trim();
    if (!name) { flash(tr("admin.emptyName"), true); return; }
    const r = await authFetch(`${API}/admin/themes/${renameThemeModal.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    if (!r.ok) { const d = await safeJson(r); return flash(d.error || tr("admin.renameError"), true); }
    await fetchThemes(); setRenameThemeModal(null); flash(tr("admin.themeRenamed"));
  }

  // ── Transfer (move / copy a theme or a question) ───────────────────────────
  function openTransfer(kind, action, sourceId, defaults = {}) {
    setTransferModal({ kind, action, sourceId, targetQtId: "", targetThemeId: "", newName: "", ...defaults });
  }

  async function executeTransfer() {
    const { kind, action, sourceId, targetQtId, targetThemeId, newName } = transferModal;
    if (!targetQtId) { flash(tr("admin.pickTargetType"), true); return; }

    if (kind === "bulk") {
      if (!targetThemeId) { flash(tr("admin.pickTargetTheme"), true); return; }
      await bulkAction(action, targetThemeId);
      return;
    }

    if (kind === "theme") {
      if (action === "move") {
        const r = await authFetch(`${API}/admin/themes/${sourceId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ quiz_type_id: parseInt(targetQtId) }),
        });
        if (!r) return flash(tr("admin.networkError"), true);
        const d = await safeJson(r);
        if (!r.ok) return flash(d.error || tr("admin.error"), true);
        await Promise.all([fetchThemes(), fetchQuestions()]);
        setTransferModal(null);
        setSelectedQuizTypeId(parseInt(targetQtId)); setView("quiz-type");
        flash(tr("admin.themeMoved"));
      } else {
        const r = await authFetch(`${API}/admin/themes/${sourceId}/copy`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ quiz_type_id: parseInt(targetQtId), name: newName.trim() || undefined }),
        });
        if (!r) return flash(tr("admin.networkError"), true);
        const d = await safeJson(r);
        if (!r.ok) return flash(d.error || tr("admin.error"), true);
        await Promise.all([fetchThemes(), fetchQuestions()]);
        setTransferModal(null);
        flash(tr("admin.themeCopied", { count: d.copied }));
      }
    } else {
      // kind === "question"
      if (!targetThemeId) { flash(tr("admin.pickTargetTheme"), true); return; }
      if (action === "move") {
        // Move = update question's theme_id via PUT
        const q = questions.find(q => q.id === sourceId);
        if (!q) return;
        const r = await authFetch(`${API}/admin/questions/${sourceId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...q, answers: q.answers, theme_id: parseInt(targetThemeId) }),
        });
        if (!r) return flash(tr("admin.networkError"), true);
        const d = await safeJson(r);
        if (!r.ok) return flash(d.error || tr("admin.error"), true);
        await fetchQuestions();
        setTransferModal(null);
        flash(tr("admin.questionMoved"));
      } else {
        // Copy = POST /admin/questions/:id/copy
        const r = await authFetch(`${API}/admin/questions/${sourceId}/copy`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ theme_id: parseInt(targetThemeId) }),
        });
        if (!r) return flash(tr("admin.networkError"), true);
        const d = await safeJson(r);
        if (!r.ok) return flash(d.error || tr("admin.error"), true);
        await fetchQuestions();
        setTransferModal(null);
        flash(tr("admin.questionCopied"));
      }
    }
  }

  // ── Questions ─────────────────────────────────────────────────────────────
  function deriveTypeFromTheme(themeId) {
    const t = themes.find(th => th.id === parseInt(themeId));
    const qt = t ? QUIZ_TYPES.find(q => q.id === t.quiz_type_id) : null;
    if (!qt) return "";
    if (qt.answerType === "both") return "";   // Blind Test: user chooses
    return qt.answerType;                       // "libre" | "multiple" | "none"
  }

  function openNew() {
    setEditingId(null);
    const themeId = selectedThemeId ? String(selectedThemeId) : "";
    const autoType = themeId ? deriveTypeFromTheme(themeId) : "";
    const initAnswers = autoType === "multiple" ? ["", "", "", ""] : ["", "", "", ""];
    setForm({ ...EMPTY_FORM, theme_id: themeId, type: autoType, answers: initAnswers });
    setView("editor");
  }
  function openEdit(q) {
    setEditingId(q.id);
    const rawType = q.type || deriveTypeFromTheme(q.theme_id);
    const padded = [...q.answers];
    // scattergories: answers = categories, do not pad to 4
    if ((q.format || "classic") !== "petit_bac") { while (padded.length < 4) padded.push(""); }
    // Hémicycle : la paire « Froid / Chaud » s'édite en deux champs
    const pairTheme = themes.find(t => t.id === q.theme_id);
    if (QUIZ_TYPES.find(qt => qt.id === pairTheme?.quiz_type_id)?.contentType === "pairs") {
      const [l = "", r = ""] = String(q.question || "").split("/").map(s => s.trim());
      padded.splice(0, 2, l, r);
    }
    setForm({ short_title: q.short_title || "", question: q.question, answers: padded,
      correct_answer: q.correct_answer, difficulty: String(q.difficulty),
      theme_id: String(q.theme_id), type: rawType, media_url: q.media_url || "",
      format: q.format || "classic" });
    setView("editor");
  }
  async function saveQuestion() {
    const { short_title, question, answers, correct_answer, difficulty, theme_id, type, format } = form;
    if (!theme_id) { flash(tr("admin.pickCategory"), true); return; }

    const saveTheme = themes.find(t => t.id === parseInt(theme_id));
    const saveQT = saveTheme ? QUIZ_TYPES.find(q => q.id === saveTheme.quiz_type_id) : null;

    // ── Blind Test : rien n'est obligatoire ──────────────────────────────────
    // On prépare souvent la fiche avant d'avoir le son, et une question sans
    // audio est simplement écartée au lancement de la partie.
    if (saveQT?.contentType === "audio") {
      // Titre, artiste, média : chaque case garde sa place, même vide.
      return submitQuestion({
        short_title, question: "", answers: [0, 1, 2].map(i => (answers[i] || "").trim()), correct_answer: 0,
        difficulty: 1, theme_id: parseInt(theme_id), type: "libre",
        media_url: form.media_url, format: "classic",
      });
    }

    const resolvedType = type || deriveTypeFromTheme(theme_id);
    if (!resolvedType) { flash(tr("admin.pickAnswerType"), true); return; }

    // ── Special formats: dedicated validation and payload ────────────────────
    if (format === "petit_bac") {
      if (!question.trim()) { flash(tr("admin.fillPrompt"), true); return; }
      const cats = answers.filter(c => c && c.trim()).map(c => c.trim());
      if (cats.length < 1) { flash(tr("admin.addOneCategory"), true); return; }
      return submitQuestion({
        short_title, question: question.trim(), answers: cats, correct_answer: 0,
        difficulty: 1, theme_id: parseInt(theme_id), type: "libre",
        media_url: form.media_url || null, format,
      });
    }
    if (format === "chrono") {
      const imgs = answers.filter(a => a && a.trim());
      if (imgs.length !== 4) { flash(tr("admin.upload4Images"), true); return; }
      if (!difficulty) { flash(tr("admin.pickDifficulty"), true); return; }
      return submitQuestion({
        short_title, question: question.trim() || "", answers: imgs, correct_answer: 0,
        difficulty: parseInt(difficulty), theme_id: parseInt(theme_id), type: "libre",
        media_url: null, format,
      });
    }

    // ── Hémicycle : deux extrêmes, réunis en « Gauche / Droite » ─────────────
    // Le jeu relit la paire en coupant au « / » : un extrême ne peut pas en contenir.
    if (saveQT?.contentType === "pairs") {
      const [l, r] = [answers[0], answers[1]].map(s => (s || "").trim());
      if (!l || !r || /\//.test(l + r)) { flash(tr("admin.fillPair"), true); return; }
      return submitQuestion({
        short_title: `${l} / ${r}`, question: `${l} / ${r}`, answers: [], correct_answer: 0,
        difficulty: 1, theme_id: parseInt(theme_id), type: "none", media_url: null, format: "classic",
      });
    }

    const editorTheme = themes.find(t => t.id === parseInt(theme_id));
    const qt = editorTheme ? QUIZ_TYPES.find(q => q.id === editorTheme.quiz_type_id) : null;
    const isNone = resolvedType === "none";
    const isLibre = resolvedType === "libre";
    const isFileContent = qt?.contentType === "image" || qt?.contentType === "audio";

    // Question text required except for image/audio (the file IS the content)
    if (!isFileContent && !question.trim()) { flash(qt?.contentType === "cards" ? tr("admin.fillCardTitle") : tr("admin.fillQuestion"), true); return; }
    // Smash or Pass : l'image est la carte, elle est donc obligatoire
    if (qt?.contentType === "cards" && !form.media_url) { flash(tr("admin.uploadImageFile"), true); return; }
    // ...and for image/audio, the file is mandatory
    if (isFileContent && !form.media_url) {
      flash(qt?.contentType === "audio" ? tr("admin.uploadAudioFile") : tr("admin.uploadImageFile"), true);
      return;
    }
    // Difficulty required except for none types (Pictionnary, Bombe)
    if (!isNone && !difficulty) { flash(tr("admin.pickDifficulty"), true); return; }

    // Réponse libre : indice 0 = réponse canonique. Pour les types à alias
    // (Pixel Guess / Références) on garde aussi les variantes acceptées ;
    // sinon, une seule réponse.
    const filtered = isLibre
      ? (qt?.aliases ? answers.filter(a => a.trim()) : answers.slice(0, 1).filter(a => a.trim()))
      : answers.filter(a => a.trim());
    if (!isNone) {
      if (isLibre && filtered.length < 1) { flash(tr("admin.fillRealAnswer"), true); return; }
      if (!isLibre && filtered.length < 2) { flash(tr("admin.fillTwoAnswers"), true); return; }
    }
    return submitQuestion({
      // Pictionnary et Pixel Guess n'ont pas de titre court à saisir : le mot,
      // ou la réponse canonique, fait office de titre.
      short_title: qt?.contentType === "words" ? null : qt?.pixelated ? filtered[0] : short_title,
      question: question.trim() || "",
      answers: isNone ? [] : filtered,
      correct_answer: (isNone || isLibre) ? 0 : parseInt(correct_answer),
      difficulty: isNone ? 1 : parseInt(difficulty),
      theme_id: parseInt(theme_id), type: resolvedType,
      media_url: form.media_url || null,
      format: "classic",
    });
  }
  async function submitQuestion(payload) {
    const url = editingId ? `${API}/admin/questions/${editingId}` : `${API}/admin/questions`;
    const r = await authFetch(url, {
      method: editingId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.error"), true);
    flash(tr("admin.saved")); await fetchQuestions();
    setTimeout(() => setView("library"), 700);
  }
  // ── Bulk actions ───────────────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function bulkAction(action, themeId = null) {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const r = await authFetch(`${API}/admin/questions/bulk`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ids, theme_id: themeId ? parseInt(themeId) : undefined }),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.error"), true);
    setSelectedIds(new Set());
    setTransferModal(null);
    await fetchQuestions();
    const keys = { move: "bulkMoved", copy: "bulkCopied", duplicate: "bulkDuplicated", delete: "bulkDeleted" };
    flash(tr(`admin.${keys[action]}`, { count: d.processed }));
  }

  async function bulkDelete() {
    const n = selectedIds.size;
    if (!await confirmDialog(tr("admin.confirmBulkDelete", { count: n }))) return;
    await bulkAction("delete");
  }

  async function bulkDuplicate() {
    await bulkAction("duplicate");
  }

  async function deleteQuestion() {
    if (!editingId || !await confirmDialog(tr("admin.confirmDeleteQuestion"))) return;
    const r = await authFetch(`${API}/admin/questions/${editingId}`, { method: "DELETE" });
    if (r?.ok) { await fetchQuestions(); setView("library"); }
  }

  // ── Users ─────────────────────────────────────────────────────────────────
  function openNewUser()   { setEditingUser(null); setUserForm(EMPTY_USER); setShowUserModal(true); }
  function openEditUser(u) { setEditingUser(u.id); setUserForm({ username: u.username, email: u.email || "", role: u.role, password: "", question_limit: u.question_limit ?? "" }); setShowUserModal(true); }
  async function saveUser() {
    const { username, email, role, password, question_limit } = userForm;
    if (!username.trim() || !role) { flash(tr("admin.fillNameAndRole"), true); return; }
    const needsPassword = ["superadmin", "admin"].includes(role);
    if (!editingUser && needsPassword && !password?.trim()) { flash(tr("admin.passwordRequiredForRole"), true); return; }
    const url = editingUser ? `${API}/admin/users/${editingUser}` : `${API}/admin/users`;
    const body = { username: username.trim(), email: email.trim() || null, role, question_limit: question_limit === "" ? null : question_limit };
    if (password?.trim()) body.password = password.trim();
    const r = await authFetch(url, {
      method: editingUser ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.error"), true);
    flash(editingUser ? tr("admin.userUpdated") : tr("admin.userCreated"));
    // Un nouveau mot de passe referme la demande de réinitialisation côté
    // serveur : la liste et la pastille doivent suivre, sans quoi une demande
    // déjà traitée reste affichée jusqu'au prochain passage sur l'écran.
    await fetchUsers();
    if (isSuperAdmin && body.password) { await fetchPasswordResets(); fetchDashboard(); }
    setShowUserModal(false);
  }
  async function deleteUser(id, name) {
    if (!await confirmDialog(`Supprimer l'utilisateur "${name}" ?`)) return;
    const r = await authFetch(`${API}/admin/users/${id}`, { method: "DELETE" });
    if (r?.ok) { await fetchUsers(); flash(tr("admin.userDeleted")); }
  }

  // ── Settings ──────────────────────────────────────────────────────────────
  async function saveSettings() {
    const r = await authFetch(`${API}/admin/settings`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settingsForm),
    });
    if (r?.ok) { flash(tr("admin.settingsSaved")); await fetchSettings(); }
    else flash(tr("admin.saveError"), true);
  }

  // ── Backups ───────────────────────────────────────────────────────────────
  async function createBackup() {
    const r = await authFetch(`${API}/admin/backups`, { method: "POST" });
    if (r?.ok) { await fetchBackups(); flash(tr("admin.backupCreated")); }
    else flash(tr("admin.backupError"), true);
  }
  async function deleteBackup(name) {
    if (!await confirmDialog(tr("admin.confirmDeleteBackup", { name }))) return;
    const r = await authFetch(`${API}/admin/backups/${name}`, { method: "DELETE" });
    if (r?.ok) { await fetchBackups(); flash(tr("admin.backupDeleted")); }
  }
  // Réactive une sauvegarde de la liste : son contenu redevient la base courante.
  async function restoreBackup(name) {
    if (!await confirmDialog(tr("admin.confirmRestoreBackup", { name }))) return;
    setBackupBusy(tr("admin.backupRestoring"));
    const r = await authFetch(`${API}/admin/backups/${name}/restore`, { method: "POST" });
    setBackupBusy("");
    if (!r) return flash(tr("admin.networkError"), true);
    const d = await safeJson(r);
    if (!r.ok) return flash(d.error || tr("admin.backupRestoreError"), true);
    await Promise.all([fetchBackups(), fetchContent()]);
    flash(tr("admin.backupRestored", { name: d.safety }));
  }
  // Import d'un fichier .db : il rejoint la liste, puis on restaure dans la foulée.
  async function importBackup(file) {
    if (!file) return;
    if (!await confirmDialog(tr("admin.confirmRestoreUpload", { name: file.name }))) return;
    setBackupBusy(tr("admin.backupImporting"));
    const fd = new FormData();
    fd.append("file", file);
    const r = await authFetch(`${API}/admin/backups/upload`, { method: "POST", body: fd });
    if (!r) { setBackupBusy(""); return flash(tr("admin.networkError"), true); }
    const d = await safeJson(r);
    if (!r.ok) { setBackupBusy(""); await fetchBackups(); return flash(d.error || tr("admin.backupImportError"), true); }

    setBackupBusy(tr("admin.backupRestoring"));
    const rr = await authFetch(`${API}/admin/backups/${d.name}/restore`, { method: "POST" });
    setBackupBusy("");
    await Promise.all([fetchBackups(), fetchContent()]);
    if (!rr) return flash(tr("admin.networkError"), true);
    const dd = await safeJson(rr);
    if (!rr.ok) return flash(dd.error || tr("admin.backupRestoreError"), true);
    flash(tr("admin.backupRestored", { name: dd.safety }));
  }

  // ── Logs ──────────────────────────────────────────────────────────────────
  async function clearLogs() {
    if (!await confirmDialog(tr("admin.confirmClearLogs"))) return;
    const r = await authFetch(`${API}/admin/logs`, { method: "DELETE" });
    if (r?.ok) { setLogs([]); flash(tr("admin.logsCleared")); }
  }
  async function changeLogFilter(f) {
    setLogFilter(f);
    await fetchLogs(f);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  // ── Login ─────────────────────────────────────────────────────────────────
  // Not signed in with admin rights (no session, or a plain "user" account):
  // show the login. Signing in here replaces the shared account.
  if (!isAdmin) {
    return (
      <div className="adm-login-page">
        <Navigation />
        <div className="adm-login-card">
          <h2 className="adm-login-title">{tr("admin.loginTitle")}</h2>
          <input className={`adm-input${loginError ? " is-error" : ""}`} type="text" placeholder={tr("admin.loginId")} value={username} onChange={e => setUsername(e.target.value)} autoFocus />
          <input className={`adm-input${loginError ? " is-error" : ""}`} type="password" placeholder={tr("admin.password")} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && login()} />
          {loginError && <p className="adm-login-error">{loginError}</p>}
          <button className="btn btn-primary adm-login-btn" onClick={login}>{tr("admin.loginBtn")}</button>
        </div>
      </div>
    );
  }

  // ── Derived data ──────────────────────────────────────────────────────────
  const isSuperAdmin = account?.role === "superadmin";

  const themeCount = {};
  questions.forEach(q => { themeCount[q.theme_id] = (themeCount[q.theme_id] || 0) + 1; });

  const scopedQuestions = selectedThemeId
    ? questions.filter(q => q.theme_id === selectedThemeId)
    : selectedQuizTypeId
    ? questions.filter(q => themes.find(t => t.id === q.theme_id)?.quiz_type_id === selectedQuizTypeId)
    : questions;

  const needle = searchQuery.trim().toLowerCase();
  const filteredQuestions = needle
    ? scopedQuestions.filter(q =>
        (q.short_title || "").toLowerCase().includes(needle) ||
        (q.question || "").toLowerCase().includes(needle) ||
        (Array.isArray(q.answers) ? q.answers : []).some(a => String(a).toLowerCase().includes(needle)))
    : scopedQuestions;

  const libraryTitle = selectedThemeId
    ? themes.find(t => t.id === selectedThemeId)?.name || tr("admin.questions")
    : selectedQuizTypeId
    ? quizTypes.find(qt => qt.id === selectedQuizTypeId)?.name || tr("admin.questions")
    : tr("admin.myQuestions");

  // A Blind Test theme is selected -> allow importing songs through yt-dlp
  const selectedThemeObj = selectedThemeId ? themes.find(t => t.id === selectedThemeId) : null;
  // Thème verrouillé : plus d'ajout pour les admins (le superadmin garde la main).
  const themeClosed = !!selectedThemeObj?.locked && !isSuperAdmin;
  const selectedThemeContent = selectedThemeObj
    ? QUIZ_TYPES.find(qt => qt.id === selectedThemeObj.quiz_type_id)?.contentType : null;
  const isBlindTestTheme = selectedThemeObj
    && QUIZ_TYPES.find(qt => qt.id === selectedThemeObj.quiz_type_id)?.contentType === "audio";

  const SYS_NAV = [
    { id: "dashboard", icon: "bi-speedometer2",    label: tr("admin.navDashboard"), superOnly: true },
    { id: "games",     icon: "bi-controller",      label: tr("admin.navGames"),superOnly: true },
    { id: "users",     icon: "bi-people-fill",     label: tr("admin.navUsers"),    superOnly: true,
      badge: isSuperAdmin ? (dashboard?.stats?.passwordResets || 0) : 0 },
    { id: "network",   icon: "bi-globe2",          label: tr("admin.navNetwork"),          superOnly: true },
    { id: "sso",       icon: "bi-shield-lock-fill", label: tr("admin.navSso"),     superOnly: true },
    { id: "shop",      icon: "bi-bag-fill",        label: tr("admin.navShop"),     superOnly: true },
    { id: "advanced",  icon: "bi-sliders",         label: tr("admin.navAdvanced"), superOnly: true },
    { id: "reports",   icon: "bi-bug-fill",        label: tr("admin.navReports"),  superOnly: true },
    { id: "apikeys",   icon: "bi-key-fill",        label: tr("admin.navApiKeys"),  superOnly: true },
    { id: "backups",   icon: "bi-database-fill",   label: tr("admin.navBackups"),     superOnly: true },
    { id: "logs",      icon: "bi-terminal-fill",   label: tr("admin.navLogs"),            superOnly: true },
  ].filter(item => !item.superOnly || isSuperAdmin);

  // ── Sidebar ───────────────────────────────────────────────────────────────
  const sidebar = (
    <>
      {/* L'en-tête du jeu, comme partout : le menu à la place du logo, le
          profil à droite. Le bouton se change lui-même en croix — même place,
          même geste pour ouvrir et fermer. */}
      <div className="adm-header">
        <Navigation {...navProps} menuButton={
          <button type="button" className={`btn adm-burger${navOpen ? " open" : ""}`}
            onClick={() => setNavOpen(o => !o)} aria-label={tr("admin.navMenu")} aria-expanded={navOpen}>
            <span /><span /><span />
          </button>
        } />
      </div>
      {navOpen && <div className="adm-nav-scrim" onClick={() => setNavOpen(false)} />}
    <aside className={`adm-sidebar${navOpen ? " open" : ""}`}>
      <div className="adm-sidebar-body">
        {/* Sortie franche vers le jeu : le logo y mène aussi, mais un panneau
            d'administration se quitte trop souvent pour dépendre d'un clic
            qu'on devine. */}
        <button className="adm-home-btn" onClick={onBack}>
          <i className="bi bi-house-door-fill"></i>
          <span>{tr("admin.backToGame")}</span>
        </button>

        {/* Questiothèque : créer, parcourir les types et les thèmes. */}
        {!isSystemSection && (<>
        <div className="adm-create-wrapper" ref={createRef}>
          <button className="btn btn-primary adm-create-btn" onClick={() => setCreateOpen(v => !v)}>
            {tr("admin.create")} <i className={`bi bi-chevron-${createOpen ? "up" : "down"}`}></i>
          </button>
          {createOpen && (
            <div className="adm-create-dropdown">
              <button className="adm-create-option" onClick={() => { setShowThemeModal(true); setCreateOpen(false); }}>
                <i className="bi bi-tag-fill"></i> {tr("admin.theme")}
              </button>
              <button className="adm-create-option" onClick={() => { openNew(); setCreateOpen(false); }}>
                <i className="bi bi-question-circle-fill"></i> Question
              </button>
            </div>
          )}
        </div>

        {/* All questions button */}
        <button
          className={`adm-all-questions-btn${!selectedQuizTypeId && !selectedThemeId && view === "library" ? " active" : ""}`}
          onClick={() => { setSelectedQuizTypeId(null); setSelectedThemeId(null); setView("library"); closeNavOnMobile(); }}
        >
          <i className="bi bi-list-ul"></i>
          <span>{tr("admin.allQuestions")}</span>
          <span className="adm-theme-count">{questions.length}</span>
        </button>

        {/* Quiz types tree */}
        <ul className="adm-quiztype-list">
          {quizTypes.map(qt => {
            const qtThemes = themes.filter(t => t.quiz_type_id === qt.id);
            const isOpen = selectedQuizTypeId === qt.id;
            return (
              <li key={qt.id}>
                <div
                  className={`adm-quiztype-item${isOpen ? " active" : ""}`}
                  onClick={() => {
                    if (isOpen && view === "quiz-type") { setSelectedQuizTypeId(null); setView("library"); }
                    else { setSelectedQuizTypeId(qt.id); setSelectedThemeId(null); setView("quiz-type"); closeNavOnMobile(); }
                  }}
                >
                  <span>{qt.name}</span>
                  {/* Total du mode : la somme de ses thèmes, comme les pastilles en dessous. */}
                  <span className="adm-theme-right">
                    {(() => { const n = qtThemes.reduce((sum, t) => sum + (themeCount[t.id] || 0), 0);
                      return n ? <span className="adm-theme-count">{n}</span> : null; })()}
                    <i className={`bi bi-chevron-${isOpen ? "down" : "right"}`}></i>
                  </span>
                </div>
                {isOpen && (
                  <ul className="adm-theme-sublist">
                    {qtThemes.map(t => (
                      <li key={t.id} className={`adm-theme-item${selectedThemeId === t.id ? " active" : ""}`}
                        onClick={() => { setSelectedThemeId(t.id); setView("library"); closeNavOnMobile(); }}>
                        <span>{t.name}</span>
                        <span className="adm-theme-right">
                          {themeCount[t.id] ? <span className="adm-theme-count">{themeCount[t.id]}</span> : null}
                          <i className="bi bi-chevron-right"></i>
                        </span>
                      </li>
                    ))}
                    {qtThemes.length === 0 && <li className="adm-theme-empty">{tr("admin.noTheme")}</li>}
                  </ul>
                )}
              </li>
            );
          })}
          {quizTypes.length === 0 && <li className="adm-quiztype-hint">{tr("admin.createQuizType")}</li>}
        </ul>
        </>)}

        {/* Administration : les réglages du serveur, superadmin seulement. */}
        {isSystemSection && (
          <ul className="adm-sys-nav">
            {SYS_NAV.map(item => (
              <li key={item.id}
                className={`adm-sys-nav-item${view === item.id ? " active" : ""}`}
                onClick={() => goTo(item.id)}>
                <i className={`bi ${item.icon}`}></i>
                <span>{item.label}</span>
                {!!item.badge && <span className="adm-theme-count">{item.badge}</span>}
              </li>
            ))}
            {SYS_NAV.length === 0 && <li className="adm-quiztype-hint">{tr("admin.systemSuperOnly")}</li>}
          </ul>
        )}
      </div>

      <button className="adm-logout-btn" onClick={logout}>
        <i className="bi bi-box-arrow-left"></i> {tr("admin.logout")}
      </button>
    </aside>
    </>
  );

  // ── Reusable: message toast ───────────────────────────────────────────────
  const MsgBanner = () => msg.text
    ? <div className={`adm-msg${msg.error ? " adm-msg-error" : ""}`}>{msg.text}</div>
    : null;

  // ── Modals (theme + quiz type, shared by the library and the editor) ───────
  const transferTargetThemes = transferModal
    ? themes.filter(t => t.quiz_type_id === parseInt(transferModal.targetQtId))
    : [];

  // "Player view" preview of the question being edited
  const testFmt = form.format || "classic";
  const testType = testFmt === "petit_bac" ? "libre" : (form.type || deriveTypeFromTheme(form.theme_id));
  const testTheme = themes.find(t => t.id === parseInt(form.theme_id));
  const testQT = testTheme ? QUIZ_TYPES.find(qt => qt.id === testTheme.quiz_type_id) : null;
  const testIsImage = form.media_url && /\.(jpe?g|png|gif|webp)$/i.test(form.media_url);
  const testAnswers = form.answers.filter(a => a.trim());
  const chronoCorrect = testFmt === "chrono" && testOrder.length === 4
    && testOrder.every((url, i) => url === testAnswers[i]);
  const testModal = testMode && (
    <div className="adm-modal-overlay" onClick={() => setTestMode(false)}>
      <div className="adm-modal adm-test-modal" onClick={e => e.stopPropagation()}>
        <div className="adm-test-header">
          <span className="adm-test-badge"><i className="bi bi-eye-fill"></i> {tr("admin.playerPreview")}</span>
          <span className={`adm-test-timer${testTimer <= 5 && !testRevealed ? " urgent" : ""}`}>
            {testRevealed ? <i className="bi bi-check-lg"></i> : `${testTimer}s`}
          </span>
        </div>
        {form.media_url && (
          testQT?.pixelated && testIsImage
            ? <PixelPreview src={`${API}${form.media_url}`} />
            : testIsImage
            ? <img src={`${API}${form.media_url}`} alt="" className="adm-test-media" />
            : /\.(mp4|webm)$/i.test(form.media_url)
            ? <video controls src={`${API}${form.media_url}`} className="adm-test-media" />
            : <audio controls src={`${API}${form.media_url}`} className="adm-test-audio" />
        )}
        {form.question.trim() && <h3 className="adm-test-question">{form.question}</h3>}
        {testFmt === "petit_bac"
          ? <span className="adm-test-pts">{tr("admin.onePointPerCategoryHost")}</span>
          : form.difficulty && <span className="adm-test-pts">{form.difficulty} pt{parseInt(form.difficulty) > 1 ? "s" : ""}</span>}

        {testFmt === "petit_bac" && (
          <div className="pb-board">
            {form.answers.filter(c => c && c.trim()).map((cat, i) => (
              <label key={i} className="pb-row">
                <span className="pb-label">{cat}</span>
                <input value={testPb[i] || ""} disabled={testRevealed} placeholder="…"
                  onChange={e => setTestPb(a => { const n = [...a]; n[i] = e.target.value; return n; })} />
              </label>
            ))}
            {testRevealed && (
              <p className="adm-test-reveal">{tr("admin.hostValidatesCategories")}</p>
            )}
          </div>
        )}

        {testFmt === "chrono" && (
          <>
            <p className="adm-answers-hint" style={{ textAlign: "center", margin: 0 }}>
              {tr("admin.dragImages")}
            </p>
            <div className="adm-chrono-play">
              {testOrder.map((url, i) => {
                const okPos = testRevealed && url === testAnswers[i];
                return (
                  <div key={url} draggable={!testRevealed}
                    className={`adm-chrono-play-item${testRevealed ? (okPos ? " correct" : " wrong") : ""}`}
                    onDragStart={() => { testDragIdx.current = i; }}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault();
                      const from = testDragIdx.current;
                      if (from === null || from === i) return;
                      setTestOrder(o => {
                        const next = [...o];
                        const [moved] = next.splice(from, 1);
                        next.splice(i, 0, moved);
                        return next;
                      });
                      testDragIdx.current = null;
                    }}>
                    <span className="adm-chrono-play-num">{i + 1}</span>
                    <img src={`${API}${url}`} alt={`Position ${i + 1}`} draggable={false} />
                    {testRevealed && !okPos && (
                      <span className="adm-chrono-play-fix">→ {testAnswers.indexOf(url) + 1}</span>
                    )}
                  </div>
                );
              })}
            </div>
            {testRevealed && (
              <p className="adm-test-reveal">
                {chronoCorrect ? tr("admin.perfectOrder") : tr("admin.wrongOrder")}
              </p>
            )}
          </>
        )}

        {testFmt === "classic" && testType === "multiple" && (
          <div className="adm-test-choices">
            {testAnswers.map((a, i) => {
              const isCorrect = testRevealed && i === parseInt(form.correct_answer);
              const isWrongPick = testRevealed && testChoice === i && i !== parseInt(form.correct_answer);
              return (
                <button key={i}
                  className={`adm-test-choice${testChoice === i ? " selected" : ""}${isCorrect ? " correct" : ""}${isWrongPick ? " wrong" : ""}`}
                  disabled={testRevealed}
                  onClick={() => setTestChoice(i)}>
                  {a}
                </button>
              );
            })}
          </div>
        )}
        {testFmt === "classic" && testType === "libre" && (
          <>
            <input className="adm-input" placeholder={tr("admin.yourAnswerPlaceholder")} value={testAnswer}
              disabled={testRevealed} onChange={e => setTestAnswer(e.target.value)} />
            {testRevealed && (
              <p className="adm-test-reveal">{tr("admin.correctAnswerLabel")} <strong>{testAnswers[0] || "—"}</strong></p>
            )}
          </>
        )}
        {testType === "none" && testRevealed && (
          <p className="adm-test-reveal">{tr("admin.contentLabel")} <strong>{form.question || "—"}</strong></p>
        )}

        <div className="adm-modal-actions">
          <button className="btn" onClick={() => setTestMode(false)}>{tr("admin.close")}</button>
          {!testRevealed
            ? <button className="btn btn-primary" onClick={() => setTestRevealed(true)}>{tr("admin.revealAnswer")}</button>
            : <button className="btn btn-primary" onClick={() => {
                setTestAnswer(""); setTestChoice(null); setTestRevealed(false); setTestTimer(20);
                if (testFmt === "chrono") setTestOrder([...testAnswers].sort(() => 0.5 - Math.random()));
                if (testFmt === "petit_bac") setTestPb(p => p.map(() => ""));
              }}>{tr("admin.replay")}</button>}
        </div>
      </div>
    </div>
  );

  const sharedModals = (
    <>
      {testModal}
      {transferModal && (
        <div className="adm-modal-overlay" onClick={() => setTransferModal(null)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <h3>
              {tr(transferModal.action === "move" ? "admin.transferMove" : "admin.transferCopy", {
                what: transferModal.kind === "theme" ? tr("admin.transferTheme")
                  : transferModal.kind === "bulk" ? tr("admin.transferCount", { count: selectedIds.size })
                  : tr("admin.transferQuestion"),
              })}
            </h3>
            <select className="adm-param-select" value={transferModal.targetQtId}
              onChange={e => setTransferModal({ ...transferModal, targetQtId: e.target.value, targetThemeId: "" })}>
              <option value="">{tr("admin.targetTypePlaceholder")}</option>
              {quizTypes.map(qt => <option key={qt.id} value={qt.id}>{qt.name}</option>)}
            </select>
            {["question", "bulk"].includes(transferModal.kind) && transferModal.targetQtId && (
              <select className="adm-param-select" value={transferModal.targetThemeId}
                onChange={e => setTransferModal({ ...transferModal, targetThemeId: e.target.value })}>
                <option value="">{tr("admin.targetThemePlaceholder")}</option>
                {transferTargetThemes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                {transferTargetThemes.length === 0 && <option disabled>{tr("admin.noThemeInType")}</option>}
              </select>
            )}
            {transferModal.kind === "theme" && transferModal.action === "copy" && (
              <input className="adm-input" placeholder={tr("admin.copiedThemeName")}
                value={transferModal.newName}
                onChange={e => setTransferModal({ ...transferModal, newName: e.target.value })} />
            )}
            <div className="adm-modal-actions">
              <button className="btn" onClick={() => setTransferModal(null)}>{tr("admin.cancel")}</button>
              <button className="btn btn-primary" onClick={executeTransfer}>
                {transferModal.action === "move" ? tr("admin.move") : tr("admin.copy")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showThemeModal && (
        <div className="adm-modal-overlay" onClick={() => setShowThemeModal(false)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <h3>{tr("admin.newTheme")}</h3>
            <select className="adm-param-select" value={newThemeQtId} onChange={e => setNewThemeQtId(e.target.value)}>
              <option value="">{tr("admin.pickQuizTypePlaceholder")}</option>
              {quizTypes.map(qt => <option key={qt.id} value={qt.id}>{qt.name}</option>)}
            </select>
            <input className="adm-input" placeholder={tr("admin.themeName")} value={newThemeName} onChange={e => setNewThemeName(e.target.value)} onKeyDown={e => e.key === "Enter" && createTheme()} autoFocus />
            <div className="adm-modal-actions">
              <button className="btn" onClick={() => setShowThemeModal(false)}>{tr("admin.cancel")}</button>
              <button className="btn btn-primary" onClick={createTheme}>{tr("admin.create")}</button>
            </div>
          </div>
        </div>
      )}
      {renameThemeModal && (
        <div className="adm-modal-overlay" onClick={() => setRenameThemeModal(null)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <h3>{tr("admin.renameTheme")}</h3>
            <input className="adm-input" placeholder={tr("admin.themeName")} value={renameThemeModal.name}
              onChange={e => setRenameThemeModal({ ...renameThemeModal, name: e.target.value })}
              onKeyDown={e => e.key === "Enter" && renameTheme()} autoFocus />
            <div className="adm-modal-actions">
              <button className="btn" onClick={() => setRenameThemeModal(null)}>{tr("admin.cancel")}</button>
              <button className="btn btn-primary" onClick={renameTheme}>{tr("admin.rename")}</button>
            </div>
          </div>
        </div>
      )}
      {btImport && (
        <div className="adm-modal-overlay" onClick={closeBtImport}>
          <div className="adm-modal adm-bt-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-bt-head">
              <h3><i className="bi bi-music-note-list"></i> {tr("admin.importSongs")}</h3>
              <button className="adm-bt-close" onClick={closeBtImport}><i className="bi bi-x-lg"></i></button>
            </div>

            {/* Phase 1 — entering the URL */}
            {(btImport.phase === "input" || btImport.phase === "loading") && (
              <>
                <p className="adm-bt-hint">{tr("admin.importHint")}</p>
                <input className="adm-input" placeholder={tr("admin.importUrlPlaceholder")}
                  value={btImport.url} disabled={btImport.phase === "loading"}
                  onChange={e => setBtImport(s => ({ ...s, url: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && btPreview()} autoFocus />
                {btImport.error && <p className="adm-bt-error-msg">{btImport.error}</p>}
                <div className="adm-bt-ytdlp">
                  {ytdlp
                    ? (ytdlp.installed
                        ? <span className="adm-bt-ytdlp-ok"><i className="bi bi-check-circle-fill"></i> yt-dlp v{ytdlp.version}</span>
                        : <span className="adm-bt-ytdlp-ko"><i className="bi bi-exclamation-triangle-fill"></i> {tr("admin.ytdlpUnavailable")}</span>)
                    : <span className="adm-bt-ytdlp-ok">…</span>}
                  {isSuperAdmin && (
                    <button className="adm-bt-ytdlp-update" onClick={updateYtdlp} disabled={ytdlpUpdating}>
                      <i className={`bi ${ytdlpUpdating ? "bi-arrow-repeat adm-spin" : "bi-arrow-clockwise"}`}></i>
                      {ytdlpUpdating ? tr("admin.ytdlpUpdating") : tr("admin.ytdlpUpdate")}
                    </button>
                  )}
                </div>
                <div className="adm-modal-actions">
                  <button className="btn" onClick={closeBtImport}>{tr("admin.cancel")}</button>
                  <button className="btn btn-primary" onClick={btPreview} disabled={btImport.phase === "loading" || !btImport.url.trim()}>
                    {btImport.phase === "loading" ? <><i className="bi bi-arrow-repeat adm-spin"></i> {tr("admin.analyzing")}</> : tr("admin.analyze")}
                  </button>
                </div>
              </>
            )}

            {/* Phase 2 — reviewing / fixing the tracks */}
            {btImport.phase === "review" && (() => {
              const sel = btImport.tracks.filter(t => t.selected).length;
              return (
                <>
                  {btImport.playlistTitle && <p className="adm-bt-playlist"><i className="bi bi-music-note-list"></i> {btImport.playlistTitle}</p>}
                  <div className="adm-bt-review-bar">
                    <span>{tr("admin.importSelected", { count: sel, total: btImport.tracks.length })}{sel > btImport.importMax ? tr("admin.importMaxNote", { n: btImport.importMax }) : ""}</span>
                    <span className="adm-bt-review-links">
                      <button className="adm-bt-link" onClick={() => btSelectAll(true)}>{tr("admin.all")}</button>
                      <button className="adm-bt-link" onClick={() => btSelectAll(false)}>{tr("admin.none")}</button>
                    </span>
                  </div>
                  {btImport.truncated && <p className="adm-bt-warn">{tr("admin.importTruncated")}</p>}
                  <div className="adm-bt-tracks">
                    {btImport.tracks.map((t, i) => (
                      <div key={t.id + "-" + i} className={`adm-bt-track${t.selected ? " on" : ""}`}>
                        <button className={`adm-q-check${t.selected ? " checked" : ""}`} title={tr("admin.include")} onClick={() => btToggleTrack(i)}>
                          {t.selected && <i className="bi bi-check-lg"></i>}
                        </button>
                        <div className="adm-bt-track-fields">
                          <input className="adm-bt-track-title" placeholder={tr("admin.title")} value={t.title} onChange={e => btEditTrack(i, "title", e.target.value)} />
                          <input className="adm-bt-track-artist" placeholder={tr("admin.artistOptionalShort")} value={t.artist} onChange={e => btEditTrack(i, "artist", e.target.value)} />
                        </div>
                        {t.duplicate && (
                          // Info-bulle maison : au survol, ou au toucher (le focus) sur mobile.
                          <span className="adm-bt-dup" tabIndex={0} aria-label={tr("admin.importDuplicate")}>
                            <i className="bi bi-exclamation-triangle-fill"></i>
                            <span className="adm-bt-dup-tip" role="tooltip">
                              <b>{tr("admin.importDuplicate")}</b>
                              <span>{[t.duplicate.title, t.duplicate.artist].filter(Boolean).join(" — ")}</span>
                              <span className="adm-bt-dup-theme">{t.duplicate.theme}</span>
                            </span>
                          </span>
                        )}
                        <span className="adm-bt-track-raw" title={t.rawTitle}><i className="bi bi-info-circle"></i></span>
                      </div>
                    ))}
                  </div>
                  <div className="adm-modal-actions">
                    <button className="btn" onClick={() => setBtImport(s => ({ ...s, phase: "input" }))}>{tr("admin.back")}</button>
                    <button className="btn btn-primary" onClick={btStartImport} disabled={sel === 0 || sel > btImport.importMax}>
                      Importer {sel} musique{sel !== 1 ? "s" : ""}
                    </button>
                  </div>
                </>
              );
            })()}

            {/* Phase 3 — download in progress & final summary */}
            {(btImport.phase === "importing" || btImport.phase === "done") && btImport.progress && (
              <>
                <div className="adm-bt-progress">
                  <div className="adm-bt-progress-bar">
                    <div className="adm-bt-progress-fill" style={{ width: `${Math.round((btImport.progress.done / Math.max(1, btImport.progress.total)) * 100)}%` }} />
                  </div>
                  <span className="adm-bt-progress-txt">{btImport.progress.done} / {btImport.progress.total}</span>
                </div>
                {btImport.phase === "importing" && (
                  <p className="adm-bt-current"><i className="bi bi-arrow-repeat adm-spin"></i> {btImport.progress.current || tr("admin.preparing")}</p>
                )}
                {btImport.phase === "done" && (
                  <p className="adm-bt-done-summary">
                    <i className="bi bi-check-circle-fill"></i> {tr("admin.importAdded", { count: (btImport.progress.created || []).length })}
                    {(btImport.progress.errors || []).length > 0 && <> · {tr("admin.importFailures", { count: btImport.progress.errors.length })}</>}
                  </p>
                )}
                {(btImport.progress.errors || []).length > 0 && (
                  <div className="adm-bt-errors">
                    {btImport.progress.errors.map((er, i) => (
                      <div key={i} className="adm-bt-error-row"><b>{er.title}</b> — {er.error}</div>
                    ))}
                  </div>
                )}
                {btImport.phase === "done" && (
                  <div className="adm-modal-actions">
                    <button className="btn btn-primary" onClick={closeBtImport}>{tr("admin.done")}</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );

  // ── Library ───────────────────────────────────────────────────────────────
  const DIFF_LABEL = { 1: tr("admin.easy"), 2: tr("admin.medium"), 3: tr("admin.hard") };
  const DIFF_COLOR = { 1: "#4ade80", 2: "#f8cf01", 3: "#f87171" };
  const hasSelection = selectedIds.size > 0;
  const questionCard = (q) => {
    const theme = themes.find(t => t.id === q.theme_id);
    const isSelected = selectedIds.has(q.id);
    return (
      <div key={q.id}
        className={`adm-question-card${isSelected ? " selected" : ""}${hasSelection ? " select-mode" : ""}`}
        onClick={() => hasSelection ? toggleSelect(q.id) : openEdit(q)}>
        <button className={`adm-q-check${isSelected ? " checked" : ""}`} title={tr("admin.select")}
          onClick={e => { e.stopPropagation(); toggleSelect(q.id); }}>
          {isSelected && <i className="bi bi-check-lg"></i>}
        </button>
        {/* Smash or Pass : le titre de la carte est la question, le « titre
            court » y sert de sous-titre (espèce, numéro…). Ailleurs, le titre
            court passe en premier. */}
        {(() => {
          const isCard = QUIZ_TYPES.find(qt => qt.id === theme?.quiz_type_id)?.contentType === "cards";
          const title = (isCard ? q.question : q.short_title || q.question)
            || (Array.isArray(q.answers) ? q.answers.filter(Boolean).join(" — ") : "") || "(sans titre)";
          return (<>
            <span className="adm-question-card-title">{title}</span>
            {isCard && q.short_title && <span className="adm-question-card-sub">{q.short_title}</span>}
          </>);
        })()}
        <div className="adm-question-card-badges">
          {q.format === "petit_bac" && <span className="adm-q-badge adm-q-badge-format">{tr("admin.formatPetitBac")}</span>}
          {q.format === "chrono" && <span className="adm-q-badge adm-q-badge-format">{tr("admin.formatChronoShort")}</span>}
          {q.lyrics_lrc && <span className="adm-q-badge adm-q-badge-format">{tr("admin.lyricsBadge")}</span>}
          {q.format !== "petit_bac" && q.difficulty && DIFF_LABEL[q.difficulty] && q.type !== "none" && (
            <span className="adm-q-badge" style={{ color: DIFF_COLOR[q.difficulty], backgroundColor: DIFF_COLOR[q.difficulty] + "22" }}>
              {DIFF_LABEL[q.difficulty]}
            </span>
          )}
          {q.media_url && <span className="adm-q-badge adm-q-badge-media"><i className={`bi ${/\.(mp3|wav|ogg|m4a|flac|aac)$/i.test(q.media_url) ? "bi-music-note-beamed" : "bi-image"}`}></i></span>}
          {!selectedThemeId && theme && <span className="adm-q-badge adm-q-badge-theme">{theme.name}</span>}
        </div>
      </div>
    );
  };

  if (view === "library") return (
    <div className="adm-layout">
      {sidebar}
      <main className="adm-main">
        <div className="adm-library-header">
          <h1 className="adm-library-title">{libraryTitle}</h1>
          <div className="adm-library-actions">
            {/* Mots, images à deviner, cartes : on les ajoute par paquets. */}
            {["words", "pairs", "image", "cards"].includes(selectedThemeContent) && !themeClosed && (
              <button className="btn btn-primary adm-bt-import-btn" onClick={() => setBatchOpen(true)}>
                <i className="bi bi-collection"></i> {tr("admin.batchButton")}
              </button>
            )}
            {isBlindTestTheme && !themeClosed && (
              <button className="btn btn-primary adm-bt-import-btn" onClick={() => openBtImport(selectedThemeId)}>
                <i className="bi bi-youtube"></i> {tr("admin.importSongs")}
              </button>
            )}
            {isBlindTestTheme && !themeClosed && (
              <button className="btn adm-btn-dark" onClick={async () => {
                const r = await authFetch(`${API}/admin/themes/${selectedThemeId}/lyrics/fetch-missing`, { method: "POST" });
                const d = await r?.json().catch(() => ({}));
                if (!r?.ok) return flash(d?.error || tr("admin.error"), true);
                flash(tr("admin.lyricsQueued", { n: d.queued }));
                // La recherche se fait une musique à la fois : on relit la liste un peu plus tard.
                setTimeout(fetchQuestions, Math.min(60000, 2500 * d.queued + 3000));
              }}>
                <i className="bi bi-music-note-list"></i> {tr("admin.lyricsFetchMissing")}
              </button>
            )}
            {themeClosed ? (
              <span className="adm-theme-locked-note"><i className="bi bi-lock-fill"></i> {tr("admin.themeLocked")}</span>
            ) : (
            <button className="btn btn-primary adm-new-question-btn" onClick={openNew}>
              <i className="bi bi-plus-lg"></i> {tr("admin.newQuestion")}
            </button>
            )}
            <div className="adm-search-wrap">
              <i className="bi bi-search"></i>
              <input className="adm-search-input" placeholder={tr("admin.searchQuestion")}
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              {searchQuery && (
                <button className="adm-search-clear" onClick={() => setSearchQuery("")}><i className="bi bi-x-lg"></i></button>
              )}
            </div>
          </div>
        </div>
        <MsgBanner />
        {filteredQuestions.length === 0 ? (
          <div className="adm-empty">
            <p>{needle ? tr("admin.noQuestionMatch") : tr("admin.noQuestion")}</p>
            {!needle && <button className="btn btn-primary" onClick={openNew}>{tr("admin.newQuestion")}</button>}
          </div>
        ) : (selectedThemeId || selectedQuizTypeId) ? (
          <div className="adm-questions-grid">
            {filteredQuestions.map(questionCard)}
          </div>
        ) : (
          // "All questions" view: grouped by quiz type so questions with the same
          // title but different types are not confused with one another.
          [...quizTypes, { id: null, name: tr("admin.noType") }].map(qt => {
            const qtQuestions = filteredQuestions.filter(q => themes.find(t => t.id === q.theme_id)?.quiz_type_id === qt.id);
            if (qtQuestions.length === 0) return null;
            return (
              <div key={qt.id ?? "no-type"} className="adm-question-group">
                <h2 className="adm-question-group-title">{qt.name}</h2>
                <div className="adm-questions-grid">
                  {qtQuestions.map(questionCard)}
                </div>
              </div>
            );
          })
        )}
        {hasSelection && (
          <div className="adm-bulk-bar">
            <span className="adm-bulk-count">{tr("admin.selectedCount", { count: selectedIds.size })}</span>
            <button className="btn adm-btn-dark" onClick={() => setSelectedIds(new Set(filteredQuestions.map(q => q.id)))}>
              {tr("admin.selectAll")}
            </button>
            <div className="adm-bulk-actions">
              <button className="btn adm-btn-dark" onClick={() => openTransfer("bulk", "move", null)}>
                <i className="bi bi-arrow-right-square"></i> {tr("admin.move")}
              </button>
              <button className="btn adm-btn-dark" onClick={bulkDuplicate}>
                <i className="bi bi-copy"></i> {tr("admin.duplicate")}
              </button>
              <button className="btn adm-btn-danger" onClick={bulkDelete}>
                <i className="bi bi-trash3"></i> Supprimer
              </button>
            </div>
            <button className="adm-bulk-cancel" title={tr("admin.clearSelection")} onClick={() => setSelectedIds(new Set())}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}
      </main>
      {batchOpen && selectedThemeObj && (
        <AdminBatchAdd theme={selectedThemeObj} contentType={selectedThemeContent} authFetch={authFetch} flash={flash}
          onClose={() => setBatchOpen(false)} onDone={() => { setBatchOpen(false); fetchQuestions(); }} />
      )}
      {sharedModals}
    </div>
  );

  // ── Quiz type (themes) ─────────────────────────────────────────────────────
  if (view === "quiz-type" && selectedQuizTypeId) {
    const qt = quizTypes.find(q => q.id === selectedQuizTypeId);
    const qtThemes = themes.filter(t => t.quiz_type_id === selectedQuizTypeId);
    const qtQCount = qtThemes.reduce((acc, t) => acc + (themeCount[t.id] || 0), 0);
    return (
      <div className="adm-layout">
        {sidebar}
        <main className="adm-main">
          <div className="adm-qt-header">
            <div className="adm-qt-title-block">
              <i className="bi bi-collection-fill adm-qt-icon"></i>
              <div>
                <h1 className="adm-qt-title">{qt?.name}</h1>
                <span className="adm-qt-meta">{tr("admin.themeCount", { count: qtThemes.length })} · {tr("admin.questionCount", { count: qtQCount })}</span>
              </div>
            </div>
          </div>
          <MsgBanner />
          {qtThemes.length === 0 ? (
            <div className="adm-empty">
              <p>{tr("admin.noThemeInTypeLong")}</p>
              <button className="btn btn-primary" onClick={() => { setNewThemeQtId(String(selectedQuizTypeId)); setShowThemeModal(true); }}>
                {tr("admin.createTheme")}
              </button>
            </div>
          ) : (
            <div className="adm-themes-grid">
              {qtThemes.map(t => (
                <div key={t.id} className="adm-theme-card" onClick={() => { setSelectedThemeId(t.id); setView("library"); }}>
                  <i className="bi bi-tag-fill adm-theme-card-icon"></i>
                  <span className="adm-theme-card-name">{!!t.locked && <i className="bi bi-lock-fill adm-theme-lock" title={tr("admin.themeLocked")}></i>}{t.name}</span>
                  <span className="adm-theme-card-count">{themeCount[t.id] || 0} question{(themeCount[t.id] || 0) !== 1 ? "s" : ""}</span>
                  <div className="adm-theme-card-actions" onClick={e => e.stopPropagation()}>
                    {isSuperAdmin && (
                      <button className="adm-theme-transfer-btn" title={tr("admin.rename")} onClick={() => setRenameThemeModal({ id: t.id, name: t.name })}>
                        <i className="bi bi-pencil-fill"></i>
                      </button>
                    )}
                    {isSuperAdmin && (
                      <button className="adm-theme-transfer-btn" title={tr("admin.moveTo")} onClick={() => openTransfer("theme", "move", t.id)}>
                        <i className="bi bi-arrow-right-square"></i>
                      </button>
                    )}
                    {isSuperAdmin && (
                      <button className="adm-theme-transfer-btn" title={tr("admin.copyTo")} onClick={() => openTransfer("theme", "copy", t.id)}>
                        <i className="bi bi-copy"></i>
                      </button>
                    )}
                    {isSuperAdmin && (
                      <button className={`adm-theme-transfer-btn${t.locked ? " on" : ""}`}
                        title={t.locked ? tr("admin.unlockTheme") : tr("admin.lockTheme")} onClick={() => toggleThemeLock(t)}>
                        <i className={`bi ${t.locked ? "bi-lock-fill" : "bi-unlock"}`}></i>
                      </button>
                    )}
                    {isSuperAdmin && (
                      <button className="adm-theme-transfer-btn adm-theme-delete-btn" title={tr("admin.deleteTheme")} onClick={() => deleteTheme(t)}>
                        <i className="bi bi-trash3"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="adm-theme-card adm-theme-card-add" onClick={() => { setNewThemeQtId(String(selectedQuizTypeId)); setShowThemeModal(true); }}>
                <i className="bi bi-plus-lg"></i>
                <span>{tr("admin.newTheme")}</span>
              </div>
            </div>
          )}
        </main>
        {sharedModals}
      </div>
    );
  }

  // ── Editor ────────────────────────────────────────────────────────────────
  if (view === "editor") {
    const editorTheme = themes.find(t => t.id === parseInt(form.theme_id));
    const currentQT = editorTheme ? QUIZ_TYPES.find(qt => qt.id === editorTheme.quiz_type_id) : null;
    const fmt = form.format || "classic";
    // The special formats (scattergories, chrono) only apply to the "classic" types
    const isClassicQT = !currentQT || currentQT.contentType === "classic";
    const activeFmt = isClassicQT ? fmt : "classic";
    const questionLabel = activeFmt === "petit_bac" ? tr("admin.promptPetitBac")
      : activeFmt === "chrono" ? tr("admin.promptOptional")
      : currentQT?.contentType === "words" ? tr("admin.wordToGuess")
      : currentQT?.contentType === "syllables" ? tr("admin.syllable")
      : currentQT?.contentType === "cards" ? tr("admin.cardTitle")
      : tr("admin.question");
    const effectiveAnswerType = currentQT?.answerType === "both" ? form.type : (currentQT?.answerType || form.type);

    return (
    <div className="adm-layout">
      {sidebar}
      <main className="adm-editor-main">
        {/* Même bouton Retour que dans le reste du jeu. */}
        <div className="adm-editor-back"><BackArrow onClick={() => setView("library")} /></div>
        {/* Pas de titre court là où il répéterait le contenu (Pictionnary,
            Pixel Guess) ou serait calculé (Hémicycle). */}
        {!(currentQT?.contentType === "words" || currentQT?.pixelated || currentQT?.contentType === "pairs") && (
          <input className="adm-field-title"
            placeholder={currentQT?.contentType === "cards" ? tr("admin.cardSubtitle") : tr("admin.shortTitle")} value={form.short_title} onChange={e => setForm({ ...form, short_title: e.target.value })} />
        )}
        {/* Hémicycle : l'extrême de gauche et celui de droite, placés comme en jeu */}
        {currentQT?.contentType === "pairs" && (
          <div className="adm-pair-row">
            {[0, 1].map(i => (
              <input key={i} className="adm-field-title" value={form.answers[i] || ""}
                placeholder={tr((currentQT.pairLabels || ["admin.pairLeft", "admin.pairRight"])[i])}
                onChange={e => setForm(f => { const a = [...f.answers]; a[i] = e.target.value; return { ...f, answers: a }; })} />
            ))}
          </div>
        )}
        {/* For words/syllables (answerType none), the field is still needed:
            it holds the word / syllable to guess.
            For audio (Blind Test) there is no question: the file IS the question. */}
        {currentQT?.contentType !== "audio" && !currentQT?.pixelated &&
          (effectiveAnswerType !== "none" || ["words", "syllables", "cards"].includes(currentQT?.contentType)) && (
          <textarea className="adm-field-question" placeholder={questionLabel} value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} />
        )}
        <input ref={fileInputRef} type="file"
          accept={activeFmt === "chrono" ? "image/*" : currentQT?.contentType === "audio" ? "audio/*" : currentQT?.contentType === "image" ? "image/*" : "image/*,audio/*,video/*"}
          style={{ display: "none" }}
          onChange={e => { if (e.target.files[0]) uploadMedia(e.target.files[0], chronoSlotRef.current); chronoSlotRef.current = null; e.target.value = ""; }}
        />
        {activeFmt === "chrono" && (
          <div className="adm-chrono-editor">
            <p className="adm-answers-hint">{tr("admin.upload4ImagesTitle")} <strong>{tr("admin.inChronologicalOrder")}</strong> {tr("admin.chronoNote")}</p>
            <div className="adm-chrono-slots">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className={`adm-chrono-slot${form.answers[i] ? " filled" : ""}`}
                  onClick={() => { chronoSlotRef.current = i; fileInputRef.current?.click(); }}>
                  <span className="adm-chrono-slot-num">{i + 1}</span>
                  {form.answers[i] ? (
                    <>
                      <img src={`${API}${form.answers[i]}`} alt={tr("admin.stepN", { n: i + 1 })} />
                      <button className="adm-chrono-slot-remove" onClick={e => {
                        e.stopPropagation();
                        setForm(f => { const a = [...f.answers]; a[i] = ""; return { ...f, answers: a }; });
                      }}><i className="bi bi-x-lg"></i></button>
                    </>
                  ) : (
                    <i className="bi bi-plus-lg"></i>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Une paire de l'Hémicycle n'a pas de média */}
        {activeFmt !== "chrono" && currentQT?.contentType !== "pairs" && <div
          className={`adm-upload-zone${uploadingMedia ? " uploading" : ""}`}
          onClick={() => !uploadingMedia && fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("drag-over"); }}
          onDragLeave={e => e.currentTarget.classList.remove("drag-over")}
          onDrop={e => {
            e.preventDefault();
            e.currentTarget.classList.remove("drag-over");
            const file = e.dataTransfer.files[0];
            if (file) uploadMedia(file);
          }}
        >
          {form.media_url ? (
            <>
              {/\.(jpe?g|png|gif|webp)$/i.test(form.media_url) ? (
                <img src={`${API}${form.media_url}`} alt={tr("admin.media")} className="adm-upload-preview" />
              ) : /\.(mp4|webm)$/i.test(form.media_url) ? (
                <video controls src={`${API}${form.media_url}`} className="adm-upload-preview" onClick={e => e.stopPropagation()} />
              ) : (
                <audio controls src={`${API}${form.media_url}`} className="adm-upload-audio" onClick={e => e.stopPropagation()} />
              )}
              <button className="adm-upload-remove" onClick={e => { e.stopPropagation(); setForm(f => ({ ...f, media_url: "" })); }}>
                <i className="bi bi-x-lg"></i> Supprimer
              </button>
            </>
          ) : (
            <>
              <i className={`bi ${currentQT?.contentType === "audio" ? "bi-music-note-beamed" : "bi-image"} adm-upload-icon`}></i>
              <div className="adm-upload-plus"><i className="bi bi-plus-lg"></i></div>
              <span className="adm-upload-label">
                {uploadingMedia ? tr("admin.uploading")
                  : currentQT?.contentType === "audio" ? tr("admin.uploadAudio")
                  : currentQT?.contentType === "image" ? tr("admin.uploadImageRequired")
                  : tr("admin.uploadMediaOptional")}
              </span>
            </>
          )}
        </div>}
        {currentQT?.contentType === "audio" && (
          <div className="adm-answers-section">
            <h4 className="adm-answers-label">{tr("admin.answerToFind")}</h4>
            <p className="adm-answers-hint">{tr("admin.blindTestHint")}</p>
            <input className="adm-answer-input" placeholder={tr("admin.songTitle")} value={form.answers[0] || ""}
              onChange={e => { const a = [...form.answers]; a[0] = e.target.value; setForm({ ...form, answers: a }); }} />
            <input className="adm-answer-input" placeholder={tr("admin.artist")} value={form.answers[1] || ""}
              onChange={e => { const a = [...form.answers]; a[1] = e.target.value; setForm({ ...form, answers: a }); }} />
            {/* Le film, la série, le jeu d'où sort la musique : une case de plus
                à deviner. Parfois la seule, quand le morceau n'a pas de titre. */}
            <input className="adm-answer-input" placeholder={tr("admin.mediaName")} value={form.answers[2] || ""}
              onChange={e => { const a = [...form.answers]; a[2] = e.target.value; setForm({ ...form, answers: a }); }} />
          </div>
        )}
        {/* Paroles minutées (Blind Test « Paroles ») : sur une fiche déjà enregistrée */}
        {currentQT?.contentType === "audio" && editingId && (
          <AdminLyrics question={questions.find(q => q.id === editingId)} mediaUrl={form.media_url}
            authFetch={authFetch} flash={flash} onSaved={fetchQuestions} />
        )}
        {activeFmt === "petit_bac" && (
          <div className="adm-answers-section">
            <h4 className="adm-answers-label">{tr("admin.categories")}</h4>
            <p className="adm-answers-hint">{tr("admin.petitBacHint")} <strong>{tr("admin.onePointPerCategory")}</strong>.</p>
            {form.answers.map((cat, i) => (
              <div key={i} className="adm-answer-row">
                <input className="adm-answer-input" placeholder={tr("admin.categoryN", { n: i + 1 })} value={cat}
                  onChange={e => { const a = [...form.answers]; a[i] = e.target.value; setForm({ ...form, answers: a }); }} />
                <button className="adm-icon-btn adm-icon-btn-danger" title={tr("admin.remove")}
                  onClick={() => setForm({ ...form, answers: form.answers.filter((_, j) => j !== i) })}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            ))}
            {form.answers.length < 12 && (
              <button className="btn adm-btn-dark adm-pb-add" onClick={() => setForm({ ...form, answers: [...form.answers, ""] })}>
                <i className="bi bi-plus-lg"></i> {tr("admin.addCategory")}
              </button>
            )}
          </div>
        )}
        {activeFmt === "classic" && currentQT?.contentType !== "audio" && effectiveAnswerType === "libre" && (
          <div className="adm-answers-section">
            <h4 className="adm-answers-label">{tr("admin.realAnswer")}</h4>
            <p className="adm-answers-hint">{tr("admin.freeAnswerNote")}</p>
            <input className="adm-answer-input adm-answer-libre" placeholder={tr("admin.realAnswerPlaceholder")} value={form.answers[0] || ""}
              onChange={e => { const a = [...form.answers]; a[0] = e.target.value; setForm({ ...form, answers: a }); }} />
            {currentQT?.aliases && (
              <div className="adm-aliases">
                <h4 className="adm-answers-label">{tr("admin.subAnswers")}</h4>
                <p className="adm-answers-hint">{tr("admin.subAnswersNote")}</p>
                {form.answers.slice(1).map((alias, i) => (
                  <div key={i} className="adm-answer-row">
                    <input className="adm-answer-input" placeholder={tr("admin.subAnswerPlaceholder")} value={alias}
                      onChange={e => { const a = [...form.answers]; a[i + 1] = e.target.value; setForm({ ...form, answers: a }); }} />
                    <button type="button" className="adm-alias-del" title={tr("admin.subAnswerRemove")}
                      onClick={() => setForm({ ...form, answers: form.answers.filter((_, j) => j !== i + 1) })}>
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                ))}
                <button type="button" className="adm-alias-add"
                  onClick={() => setForm({ ...form, answers: [...form.answers, ""] })}>
                  <i className="bi bi-plus-lg"></i> {tr("admin.subAnswerAdd")}
                </button>
              </div>
            )}
          </div>
        )}
        {activeFmt === "classic" && currentQT?.contentType !== "audio" && effectiveAnswerType === "multiple" && (
          <div className="adm-answers-section">
            <h4 className="adm-answers-label">{tr("admin.answers")}</h4>
            {form.answers.map((ans, i) => (
              <div key={i} className="adm-answer-row">
                <button className={`adm-radio-btn${form.correct_answer === i ? " correct" : ""}`} onClick={() => setForm({ ...form, correct_answer: i })} title={tr("admin.correctAnswerTitle")} />
                <input className="adm-answer-input" placeholder={tr("admin.answerN", { n: i + 1 })} value={ans}
                  onChange={e => { const a = [...form.answers]; a[i] = e.target.value; setForm({ ...form, answers: a }); }} />
              </div>
            ))}
          </div>
        )}
        <MsgBanner />
      </main>
      <aside className="adm-params-panel">
        <h3 className="adm-params-title">{tr("admin.navSettings")}</h3>
        <div className="adm-param-group">
          <label className="adm-param-label"><i className="bi bi-grid-3x3-gap-fill"></i> {tr("admin.category")}</label>
          <select className="adm-param-select" value={form.theme_id} onChange={e => {
            const newThemeId = e.target.value;
            const newTheme = themes.find(t => t.id === parseInt(newThemeId));
            const newQT = newTheme ? QUIZ_TYPES.find(qt => qt.id === newTheme.quiz_type_id) : null;
            let newType = form.type;
            if (newQT?.answerType === "multiple") newType = "multiple";
            else if (newQT?.answerType === "libre") newType = "libre";
            else if (newQT?.answerType === "none") newType = "none";
            const newAnswers = newType === "multiple" ? [...form.answers].concat(Array(Math.max(0, 4 - form.answers.length)).fill("")) : form.answers;
            setForm({ ...form, theme_id: newThemeId, type: newType, answers: newAnswers });
          }}>
            <option value="">{tr("admin.pickCategoryPlaceholder")}</option>
            {/* Once a theme (thus a mode) is chosen, only that mode's themes are offered;
                with no theme yet, every mode is listed so a starting theme can be picked. */}
            {(currentQT ? quizTypes.filter(qt => qt.id === currentQT.id) : quizTypes).map(qt => (
              <optgroup key={qt.id} label={qt.name}>
                {themes.filter(t => t.quiz_type_id === qt.id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </optgroup>
            ))}
            {!currentQT && themes.filter(t => !t.quiz_type_id).length > 0 && (
              <optgroup label={tr("admin.unclassified")}>
                {themes.filter(t => !t.quiz_type_id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        {isClassicQT && (
          <div className="adm-param-group">
            <label className="adm-param-label"><i className="bi bi-puzzle-fill"></i> {tr("admin.questionType")}</label>
            <select className="adm-param-select" value={fmt}
              onChange={e => {
                const nf = e.target.value;
                // answers is reused: 4 images (chrono) or categories (scattergories)
                // -> start from an array suited to each format change
                if (nf === "chrono") setForm({ ...form, format: nf, answers: ["", "", "", ""], correct_answer: 0, media_url: "" });
                else if (nf === "petit_bac") setForm({ ...form, format: nf, answers: [...PETIT_BAC_DEFAULTS], correct_answer: 0 });
                else setForm({ ...form, format: nf, answers: ["", "", "", ""], correct_answer: 0 });
              }}>
              {QUESTION_FORMATS.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <p className="adm-param-hint">{QUESTION_FORMATS.find(f => f.id === fmt)?.desc}</p>
          </div>
        )}
        {activeFmt === "classic" && currentQT?.answerType === "both" && currentQT?.contentType !== "audio" && (
          <div className="adm-param-group">
            <label className="adm-param-label"><i className="bi bi-ui-radios"></i> {tr("admin.answerType")}</label>
            <select className="adm-param-select" value={form.type}
              onChange={e => {
                const t = e.target.value;
                if (t === "libre") setForm({ ...form, type: t, correct_answer: 0 });
                else { const p = [...form.answers]; while (p.length < 4) p.push(""); setForm({ ...form, type: t, answers: p }); }
              }}>
              <option value="">{tr("admin.pickTypePlaceholder")}</option>
              <option value="multiple">{tr("admin.multipleChoice")}</option>
              <option value="libre">{tr("admin.freeAnswer")}</option>
            </select>
          </div>
        )}
        {activeFmt === "petit_bac" ? (
          <div className="adm-param-group">
            <label className="adm-param-label"><i className="bi bi-star-fill"></i> {tr("admin.points")}</label>
            <p className="adm-param-hint">{tr("admin.onePointPerAnswer")}</p>
          </div>
        ) : currentQT?.contentType === "audio" ? (
          <div className="adm-param-group">
            <label className="adm-param-label"><i className="bi bi-star-fill"></i> {tr("admin.points")}</label>
            <p className="adm-param-hint">{tr("admin.blindTestScoring")}</p>
          </div>
        ) : effectiveAnswerType !== "none" && (
          <div className="adm-param-group">
            <label className="adm-param-label"><i className="bi bi-star-fill"></i> {tr("admin.difficulty")}</label>
            <select className="adm-param-select" value={form.difficulty} onChange={e => setForm({ ...form, difficulty: e.target.value })}>
              <option value="">{tr("admin.pickDifficultyPlaceholder")}</option>
              <option value="1">{tr("admin.diff1")}</option>
              <option value="2">{tr("admin.diff2")}</option>
              <option value="3">{tr("admin.diff3")}</option>
            </select>
          </div>
        )}
        <div className="adm-params-actions">
          {editingId && (
            <>
              <button className="btn adm-btn-danger" onClick={deleteQuestion}>{tr("admin.delete")}</button>
              <button className="btn adm-btn-dark" onClick={() => {
                const t = themes.find(th => th.id === parseInt(form.theme_id));
                openTransfer("question", "copy", editingId, {
                  targetQtId: t?.quiz_type_id ? String(t.quiz_type_id) : "",
                  targetThemeId: form.theme_id,
                });
              }}>
                <i className="bi bi-copy"></i> Copier
              </button>
              <button className="btn adm-btn-dark" onClick={() => openTransfer("question", "move", editingId)}>
                <i className="bi bi-arrow-right-square"></i> {tr("admin.move")}
              </button>
            </>
          )}
          <button className="btn adm-btn-success" onClick={openTest}><i className="bi bi-play-fill"></i> {tr("admin.test")}</button>
          <button className="btn btn-primary adm-btn-save" onClick={saveQuestion}>{tr("admin.save")}</button>
        </div>
      </aside>
      {sharedModals}
    </div>
  );}

  // ── Games ─────────────────────────────────────────────────────────────────
  if (view === "games") {
    const STATE_LABEL = { lobby: tr("admin.stateLobby"), quiz: tr("admin.stateQuiz"), validation: tr("admin.stateValidation"), results: tr("admin.results") };
    const STATE_COLOR = { lobby: "#60a5fa", quiz: "#4ade80", validation: "#fb923c", results: "#94a3b8" };
    return (
      <div className="adm-layout">
        {sidebar}
        <main className="adm-sys-main">
          <div className="adm-sys-header">
            <h1 className="adm-sys-title"><i className="bi bi-controller"></i> {tr("admin.navGames")}</h1>
            <button className="btn adm-btn-dark adm-sys-refresh" onClick={fetchAdminGames}><i className="bi bi-arrow-clockwise"></i></button>
          </div>
          <MsgBanner />
          {adminGames.length === 0 ? (
            <p className="adm-sys-empty">{tr("admin.noActiveGame")}</p>
          ) : (
            <div className="adm-games-list">
              {adminGames.map(g => {
                const emptySecs = g.emptySince ? Math.floor((Date.now() - g.emptySince) / 1000) : null;
                return (
                  <div key={g.id} className="adm-game-row">
                    <div className="adm-game-code">{g.id}</div>
                    <span className="adm-game-state-badge" style={{ backgroundColor: (STATE_COLOR[g.state] || "#94a3b8") + "22", color: STATE_COLOR[g.state] || "#94a3b8" }}>
                      {STATE_LABEL[g.state] || g.state}
                    </span>
                    <div className="adm-game-players">
                      <i className="bi bi-people-fill"></i>
                      {tr("admin.connectedCount", { count: g.onlineCount, total: g.playerCount })}
                      {g.players.length > 0 && (
                        <span className="adm-game-player-list">
                          {g.players.map(p => (
                            <span key={p.pseudo} className={`adm-game-player-chip${p.offline ? " offline" : ""}${p.isHost ? " host" : ""}`} title={p.isHost ? "Host" : ""}>
                              {p.pseudo}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                    <div className="adm-game-meta">
                      {g.questionCount > 0 && <span>{g.questionCount} questions</span>}
                      {emptySecs !== null && (
                        <span className="adm-game-empty-warn">
                          <i className="bi bi-exclamation-triangle-fill"></i> Vide depuis {emptySecs < 60 ? `${emptySecs}s` : `${Math.floor(emptySecs / 60)}min`}
                        </span>
                      )}
                      <span className="adm-game-created">{fmt(g.createdAt)}</span>
                    </div>
                    {isSuperAdmin && (
                      <button className="btn adm-btn-danger adm-game-stop" onClick={() => forceStopGame(g.id)} title={tr("admin.forceStop")}>
                        <i className="bi bi-stop-fill"></i> {tr("admin.stop")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────
  if (view === "dashboard") return (
    <div className="adm-layout">
      {sidebar}
      <main className="adm-sys-main">
        <div className="adm-sys-header">
          <h1 className="adm-sys-title"><i className="bi bi-speedometer2"></i> {tr("admin.navDashboard")}</h1>
          <button className="btn adm-btn-dark adm-sys-refresh" onClick={fetchDashboard}><i className="bi bi-arrow-clockwise"></i></button>
        </div>
        {!dashboard ? (
          <p className="adm-sys-loading">{tr("common.loading")}</p>
        ) : (
          <>
            <div className="adm-stats-grid">
              {/* Chaque carte mène à sa page : la questiothèque pour les questions
                  et les thèmes, la liste correspondante pour le reste. */}
              {[
                { label: tr("admin.dashReports"), value: dashboard.stats.reports ?? 0, icon: "bi-bug-fill", color: "#f87171", go: () => goTo("reports") },
                { label: tr("admin.questions"), value: dashboard.stats.questions, icon: "bi-question-circle-fill", color: "#4ade80", go: () => navigate(ROUTES.admin) },
                { label: tr("admin.themes"),    value: dashboard.stats.themes,    icon: "bi-tag-fill",             color: "#60a5fa", go: () => navigate(ROUTES.admin) },
                { label: tr("admin.navUsers"),  value: dashboard.stats.users,     icon: "bi-people-fill",     color: "#f58700", go: () => goTo("users") },
                { label: tr("admin.navGames"), value: dashboard.stats.games, icon: "bi-controller",       color: "#c084fc", go: () => goTo("games") },
              ].map(s => (
                <button key={s.label} type="button" className="adm-stat-card" onClick={s.go}>
                  <i className={`bi ${s.icon}`} style={{ color: s.color }}></i>
                  <span className="adm-stat-value" style={{ color: s.color }}>{s.value}</span>
                  <span className="adm-stat-label">{s.label}</span>
                </button>
              ))}
            </div>
            <h2 className="adm-sys-section-title">{tr("admin.recentActivity")}</h2>
            <div className="adm-log-list">
              {dashboard.recentLogs.length === 0 && <p className="adm-sys-empty">{tr("admin.noActivity")}</p>}
              {dashboard.recentLogs.map(l => (
                <div key={l.id} className={`adm-log-row adm-log-${l.level}`}>
                  <span className={`adm-log-badge adm-log-badge-${l.level}`}>{l.level.toUpperCase()}</span>
                  <span className="adm-log-action">{l.action}</span>
                  {l.detail && <span className="adm-log-detail">{l.detail}</span>}
                  <span className="adm-log-time">{fmt(l.created_at)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );

  // ── Users ─────────────────────────────────────────────────────────────────
  if (view === "users") {
    const filtered = userRoleFilter === "all" ? users : users.filter(u => u.role === userRoleFilter);
    return (
      <div className="adm-layout">
        {sidebar}
        <main className="adm-sys-main">
          <div className="adm-sys-header">
            <h1 className="adm-sys-title"><i className="bi bi-people-fill"></i> {tr("admin.navUsers")}</h1>
            <button className="btn btn-primary" onClick={openNewUser}><i className="bi bi-plus"></i> {tr("admin.add")}</button>
          </div>
          <div className="adm-filter-pills">
            {["all", ...ROLES].map(r => (
              <button key={r} className={`adm-pill${userRoleFilter === r ? " active" : ""}`} onClick={() => setUserRoleFilter(r)}>
                {r === "all" ? tr("admin.allFilter") : ROLE_LABELS[r]}
              </button>
            ))}
          </div>
          <MsgBanner />
          {passwordResets.length > 0 && (
            <div className="adm-table-wrap">
              <h2 className="adm-qt-title" style={{ fontSize: "1.1rem" }}>{tr("admin.passwordResets")}</h2>
              <table className="adm-table">
                <tbody>
                  {passwordResets.map(p => (
                    <tr key={p.id}>
                      <td><div className="adm-avatar">{initials(p.username)}</div></td>
                      <td className="adm-table-bold">{p.username}</td>
                      <td className="adm-table-secondary">{tr("admin.passwordResetRequestedOn", { date: fmt(p.created_at) })}</td>
                      <td className="adm-table-actions">
                        <button className="btn adm-btn-dark"
                          onClick={() => { const u = users.find(x => x.id === p.user_id); if (u) openEditUser(u); }}>
                          {tr("admin.resetPasswordFor")}
                        </button>
                        <button className="adm-icon-btn" onClick={() => dismissPasswordReset(p.id)} title={tr("admin.dismiss")}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr>
                <th></th><th>{tr("admin.name")}</th><th>{tr("admin.email")}</th><th>{tr("admin.role")}</th><th>{tr("admin.questionsColumn")}</th><th>{tr("admin.createdAt")}</th><th>{tr("admin.lastSeen")}</th><th>{tr("admin.lastVisit")}</th><th></th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="adm-table-empty">{tr("admin.noUser")}</td></tr>
                )}
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td><div className="adm-avatar">{initials(u.username)}</div></td>
                    <td className="adm-table-bold">{u.username}</td>
                    <td className="adm-table-secondary">{u.email || "—"}</td>
                    <td><span className="adm-role-badge" style={{ backgroundColor: ROLE_COLORS[u.role] + "22", color: ROLE_COLORS[u.role] }}>{ROLE_LABELS[u.role] || u.role}</span></td>
                    <td className="adm-table-secondary">
                      {u.role === "admin" ? `${u.question_count} / ${u.question_limit ?? tr("admin.unlimited")}` : "—"}
                    </td>
                    <td className="adm-table-secondary">{fmt(u.created_at)}</td>
                    <td className="adm-table-secondary">{fmt(u.last_login)}</td>
                    <td className="adm-table-secondary">{fmt(u.last_seen || u.last_login)}</td>
                    <td className="adm-table-actions">
                      <button className="adm-icon-btn" onClick={() => setInvUserId(u.id)} title={tr("admin.invOpen")}><i className="bi bi-box-seam"></i></button>
                      <button className="adm-icon-btn" onClick={() => openEditUser(u)} title={tr("admin.edit")}><i className="bi bi-pencil-fill"></i></button>
                      <button className="adm-icon-btn adm-icon-btn-danger" onClick={() => deleteUser(u.id, u.username)} title={tr("admin.delete")}><i className="bi bi-trash3"></i></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </main>

        {invUserId && (
          <AdminInventory userId={invUserId} authFetch={authFetch} flash={flash} onClose={() => { setInvUserId(null); fetchUsers(); }} />
        )}
        {showUserModal && (
          <div className="adm-modal-overlay" onClick={() => setShowUserModal(false)}>
            <div className="adm-modal" onClick={e => e.stopPropagation()}>
              <h3>{editingUser ? tr("admin.editUser") : tr("admin.newUser")}</h3>
              <input className="adm-input" placeholder={tr("admin.username")} value={userForm.username} onChange={e => setUserForm({ ...userForm, username: e.target.value })} autoFocus />
              <input className="adm-input" placeholder={tr("admin.emailOptional")} type="email" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} />
              <select className="adm-param-select" value={userForm.role} onChange={e => setUserForm({ ...userForm, role: e.target.value, password: "" })}>
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
              {["superadmin", "admin"].includes(userForm.role) && (
                <input
                  className="adm-input"
                  placeholder={editingUser ? tr("admin.newPasswordOptional") : tr("admin.passwordRequired")}
                  type="password"
                  value={userForm.password}
                  onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                />
              )}
              {userForm.role === "admin" && (
                <input
                  className="adm-input"
                  placeholder={`${tr("admin.questionLimit")} — ${tr("admin.questionLimitHint")}`}
                  type="number" min="0"
                  value={userForm.question_limit}
                  onChange={e => setUserForm({ ...userForm, question_limit: e.target.value })}
                />
              )}
              {msg.text && <div className={`adm-msg${msg.error ? " adm-msg-error" : ""}`}>{msg.text}</div>}
              <div className="adm-modal-actions">
                <button className="btn" onClick={() => setShowUserModal(false)}>{tr("admin.cancel")}</button>
                <button className="btn btn-primary" onClick={saveUser}>{editingUser ? tr("admin.save") : tr("admin.create")}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Advanced settings ──────────────────────────────────────────────────────
  if (view === "advanced") {
    // Onglets : le général, un par mode, la boutique, la questiothèque. Dans
    // un onglet, les réglages se rangent par rubrique, l'activation du mode
    // en tête ; au-delà de deux rubriques, des filtres permettent d'en isoler une.
    const groups = gameSettings
      ? (gameSettings.tabs || [...new Set(gameSettings.schema.map(d => d.tab))])
          .filter(tab => gameSettings.schema.some(d => d.tab === tab))
      : [];
    const dirty = gameSettings && gameSettings.schema.some(d => String(gameForm[d.key]) !== String(gameSettings.values[d.key]));
    const advGroup = groups.includes(advancedGroup) ? advancedGroup : groups[0];
    const inTab = gameSettings ? gameSettings.schema.filter(d => d.tab === advGroup) : [];
    const sections = [...new Set(inTab.map(d => d.group))]
      .sort((a, b) => (b === "Activation") - (a === "Activation"));
    const shown = sections.includes(advancedSection) ? [advancedSection] : sections;
    const modeOff = inTab.some(d => d.group === "Activation" && String(gameForm[d.key] ?? d.def) === "0");
    return (
      <div className="adm-layout">
        {sidebar}
        <main className="adm-sys-main">
          <div className="adm-sys-header">
            <h1 className="adm-sys-title"><i className="bi bi-sliders"></i> {tr("admin.navAdvanced")}</h1>
            <div style={{ display: "flex", gap: ".5rem" }}>
              <button className="btn adm-btn-dark" onClick={resetGameSettings}>{tr("admin.defaultValues")}</button>
              <button className="btn btn-primary" onClick={saveGameSettings} disabled={!dirty}>{tr("admin.save")}</button>
            </div>
          </div>
          <MsgBanner />
          {!gameSettings ? (
            <p className="adm-sys-loading">{tr("common.loading")}</p>
          ) : (
            <>
              <p className="adm-advanced-intro">
                {tr("admin.advancedIntro")}
              </p>
              {/* Un onglet par groupe, comme dans le salon : la page tient à l'écran */}
              <div className="gl-tabs adm-advanced-tabs">
                {groups.map(g => (
                  <button key={g} type="button" className={`gl-tab${g === advGroup ? " active" : ""}`}
                    onClick={() => { setAdvancedGroup(g); setAdvancedSection(null); }}>{g}</button>
                ))}
              </div>
              {sections.length > 2 && (
                <div className="adm-advanced-filters">
                  {[null, ...sections].map(sec => (
                    <button key={sec || "all"} type="button"
                      className={`adm-advanced-chip${(sec === null ? shown.length === sections.length : shown.length === 1 && shown[0] === sec) ? " active" : ""}`}
                      onClick={() => setAdvancedSection(sec)}>{sec || tr("admin.advancedAll")}</button>
                  ))}
                </div>
              )}
              {modeOff && <p className="adm-advanced-off">{tr("admin.advancedModeOff")}</p>}
              {shown.map(sec => (
              <section key={sec} className="adm-advanced-group">
              <h2 className="adm-advanced-section">{sec}</h2>
              <div className="adm-advanced-grid">
                {inTab.filter(d => d.group === sec).map(d => (
                  <div key={d.key} className="adm-advanced-row">
                    <label className="adm-advanced-label" htmlFor={`adv-${d.key}`}>
                      {d.label}
                      {String(gameForm[d.key]) !== String(d.def) && (
                        <span className="adm-advanced-modified" title={tr("admin.defaultValue", { value: d.def })}>{tr("admin.modified")}</span>
                      )}
                    </label>
                    <div className="adm-advanced-input">
                      {d.type === "bool" ? (
                        <label className="adm-switch">
                          <input id={`adv-${d.key}`} type="checkbox"
                            checked={String(gameForm[d.key] ?? d.def) === "1"}
                            onChange={e => setGameForm(f => ({ ...f, [d.key]: e.target.checked ? 1 : 0 }))} />
                          <span className="adm-switch-track" aria-hidden="true" />
                        </label>
                      ) : (
                        <input
                          id={`adv-${d.key}`}
                          type={d.type === "text" ? "text" : "number"}
                          className={d.type === "text" ? "adm-advanced-text" : undefined}
                          step={d.type === "half" ? 0.5 : 1}
                          min={d.min} max={d.type === "text" ? undefined : d.max}
                          maxLength={d.type === "text" ? d.max : undefined}
                          value={gameForm[d.key] ?? d.def}
                          onChange={e => setGameForm(f => ({ ...f, [d.key]: e.target.value }))}
                        />
                      )}
                      {d.unit === "coin" || d.unit === "junk"
                        ? <Coin kind={d.unit} size={18} />
                        : d.unit && <span className="adm-advanced-unit">{d.unit}</span>}
                    </div>
                  </div>
                ))}
              </div>
              </section>
              ))}
            </>
          )}
        </main>
      </div>
    );
  }

  // ── Network ───────────────────────────────────────────────────────────────
  if (view === "network") return (
    <div className="adm-layout">
      {sidebar}
      <main className="adm-sys-main">
        <div className="adm-sys-header">
          <h1 className="adm-sys-title"><i className="bi bi-globe2"></i> {tr("admin.navNetwork")}</h1>
        </div>
        <MsgBanner />
        <div className="adm-settings-form">
          {[
            { key: "domain",     label: tr("admin.domainName"),  placeholder: "popuquiz.io",              type: "text" },
            { key: "port",       label: tr("admin.port"),            placeholder: "3000",                     type: "number" },
            { key: "public_url", label: tr("admin.publicUrl"),    placeholder: "https://popuquiz.io",       type: "url" },
          ].map(f => (
            <div key={f.key} className="adm-settings-row">
              <label className="adm-settings-label">{f.label}</label>
              <input className="adm-settings-input" type={f.type} placeholder={f.placeholder}
                value={settingsForm[f.key] || ""} onChange={e => setSettingsForm({ ...settingsForm, [f.key]: e.target.value })} />
            </div>
          ))}
          <div className="adm-settings-row">
            <label className="adm-settings-label">{tr("admin.maintenanceMode")}</label>
            <div className="adm-toggle-wrap">
              <label className="adm-toggle">
                <input type="checkbox" checked={settingsForm.maintenance === "true"}
                  onChange={e => setSettingsForm({ ...settingsForm, maintenance: e.target.checked ? "true" : "false" })} />
                <span className="adm-toggle-slider"></span>
              </label>
              <span className="adm-toggle-label">{settingsForm.maintenance === "true" ? tr("admin.enabled") : tr("admin.disabled")}</span>
            </div>
          </div>
          {/* E-mails : aujourd'hui, le mot envoyé quand un incident signalé est résolu. */}
          <h2 className="adm-settings-section">{tr("admin.smtpTitle")}</h2>
          <p className="adm-settings-hint">{tr("admin.smtpHint")}</p>
          {[
            { key: "smtp_host", label: tr("admin.smtpHost"), placeholder: "smtp.exemple.fr", type: "text" },
            { key: "smtp_port", label: tr("admin.smtpPort"), placeholder: "587", type: "number" },
            { key: "smtp_user", label: tr("admin.smtpUser"), placeholder: "popuquiz@exemple.fr", type: "text" },
            { key: "smtp_pass", label: tr("admin.smtpPass"), type: "password",
              placeholder: settingsForm.smtp_pass_set ? tr("admin.smtpPassSet") : "" },
            { key: "smtp_from", label: tr("admin.smtpFrom"), placeholder: "PopuQuiz <popuquiz@exemple.fr>", type: "text" },
          ].map(f => (
            <div key={f.key} className="adm-settings-row">
              <label className="adm-settings-label">{f.label}</label>
              <input className="adm-settings-input" type={f.type} placeholder={f.placeholder} autoComplete="off"
                value={settingsForm[f.key] || ""} onChange={e => setSettingsForm({ ...settingsForm, [f.key]: e.target.value })} />
            </div>
          ))}
          <div className="adm-settings-row">
            <label className="adm-settings-label">{tr("admin.smtpSecure")}</label>
            <div className="adm-toggle-wrap">
              <label className="adm-toggle">
                <input type="checkbox" checked={settingsForm.smtp_secure === "true"}
                  onChange={e => setSettingsForm({ ...settingsForm, smtp_secure: e.target.checked ? "true" : "false" })} />
                <span className="adm-toggle-slider"></span>
              </label>
              <span className="adm-toggle-label">{settingsForm.smtp_secure === "true" ? tr("admin.enabled") : tr("admin.disabled")}</span>
            </div>
          </div>
          <div className="adm-settings-footer">
            <button className="btn btn-primary" onClick={saveSettings}>{tr("admin.save")}</button>
          </div>
        </div>
      </main>
    </div>
  );

  // ── SSO (OpenID Connect) ──────────────────────────────────────────────────
  if (view === "sso") return (
    <div className="adm-layout">
      {sidebar}
      <main className="adm-sys-main">
        <div className="adm-sys-header">
          <h1 className="adm-sys-title"><i className="bi bi-shield-lock-fill"></i> {tr("admin.navSso")}</h1>
          <div className="adm-sys-actions">
            <button className="btn adm-btn-dark" onClick={testSso}>{tr("admin.ssoTest")}</button>
            <button className="btn btn-primary" onClick={saveSso}>{tr("admin.save")}</button>
          </div>
        </div>
        <MsgBanner />
        <p className="adm-advanced-intro">{tr("admin.ssoIntro")}</p>
        <div className="adm-settings-form">
          <div className="adm-settings-row">
            <label className="adm-settings-label">{tr("admin.ssoEnabled")}</label>
            <div className="adm-toggle-wrap">
              <label className="adm-toggle">
                <input type="checkbox" checked={ssoForm.enabled}
                  onChange={e => setSsoForm({ ...ssoForm, enabled: e.target.checked })} />
                <span className="adm-toggle-slider"></span>
              </label>
              <span className="adm-toggle-label">{ssoForm.enabled ? tr("admin.enabled") : tr("admin.disabled")}</span>
            </div>
          </div>
          <div className="adm-settings-row">
            <label className="adm-settings-label">{tr("admin.ssoIssuer")}</label>
            <input className="adm-settings-input" type="url" placeholder="https://auth.exemple.fr/application/o/popuquiz"
              value={ssoForm.issuer} onChange={e => setSsoForm({ ...ssoForm, issuer: e.target.value })} />
          </div>
          <div className="adm-settings-row">
            <label className="adm-settings-label">{tr("admin.ssoClientId")}</label>
            <input className="adm-settings-input" type="text"
              value={ssoForm.clientId} onChange={e => setSsoForm({ ...ssoForm, clientId: e.target.value })} />
          </div>
          <div className="adm-settings-row">
            <label className="adm-settings-label">{tr("admin.ssoClientSecret")}</label>
            <input className="adm-settings-input" type="password"
              placeholder={sso?.secretSet ? tr("admin.ssoSecretKeep") : tr("admin.ssoSecretRequired")}
              value={ssoForm.clientSecret} onChange={e => setSsoForm({ ...ssoForm, clientSecret: e.target.value })} />
          </div>
          <div className="adm-settings-row">
            <label className="adm-settings-label">{tr("admin.ssoLabel")}</label>
            <input className="adm-settings-input" type="text" placeholder="Authentik"
              value={ssoForm.label} onChange={e => setSsoForm({ ...ssoForm, label: e.target.value })} />
          </div>
          <div className="adm-settings-row">
            <label className="adm-settings-label">{tr("admin.ssoRedirectUri")}</label>
            <input className="adm-settings-input" type="text" readOnly value={sso?.redirectUri || ""}
              onFocus={e => e.target.select()} />
          </div>
          <div className="adm-settings-footer">
            <button className="btn btn-primary" onClick={saveSso}>{tr("admin.save")}</button>
          </div>
        </div>
      </main>
    </div>
  );

  // ── Backups ───────────────────────────────────────────────────────────────
  // ── Incidents signalés ────────────────────────────────────────────────────
  if (view === "reports") {
    const needle = reportSearch.trim().toLowerCase();
    const shown = needle
      ? reports.filter(r => [`#${r.id}`, r.title, r.comment, r.mode, r.question, r.pseudo, r.username]
          .some(v => String(v || "").toLowerCase().includes(needle)))
      : reports;
    const STATUS = { resolved: tr("admin.reportStatusResolved"), deleted: tr("admin.reportStatusDeleted") };
    return (
      <div className="adm-layout">
        {sidebar}
        <main className="adm-sys-main">
          <div className="adm-sys-header">
            <h1 className="adm-sys-title"><i className="bi bi-bug-fill"></i> {tr("admin.navReports")}</h1>
            <div className="adm-sys-actions adm-report-tools">
              <input className="adm-settings-input adm-report-search" type="search" value={reportSearch}
                placeholder={tr("admin.reportSearch")} onChange={e => setReportSearch(e.target.value)} />
              <label className="adm-report-closed">
                <input type="checkbox" checked={reportsClosed}
                  onChange={e => { setReportsClosed(e.target.checked); fetchReports(e.target.checked); }} />
                {tr("admin.reportShowClosed")}
              </label>
            </div>
          </div>
          <MsgBanner />
          {shown.length === 0 ? (
            <p className="adm-sys-empty">{reportsClosed ? tr("admin.reportNoneClosed") : tr("admin.reportNone")}</p>
          ) : (
            <div className="adm-backup-list">
              {shown.map(r => (
                <div key={r.id} className="adm-backup-row">
                  <i className="bi bi-bug-fill adm-backup-icon"></i>
                  <div className="adm-backup-info">
                    <span className="adm-backup-name"><span className="adm-report-id">#{r.id}</span> {r.title}</span>
                    {reportsClosed && <span className="adm-backup-meta">{STATUS[r.status] || r.status} · {fmt(r.closedAt)}</span>}
                  </div>
                  <div className="adm-backup-actions">
                    <button className="btn adm-btn-dark adm-backup-btn" disabled={!r.hasScreenshot}
                      onClick={() => openReportShot(r)} title={tr("admin.reportScreenshot")}>
                      <i className="bi bi-image"></i>
                    </button>
                    <button className="btn adm-btn-dark adm-backup-btn" onClick={() => setReportDetail(r)} title={tr("admin.reportDetail")}>
                      <i className="bi bi-info-circle"></i>
                    </button>
                    {r.status === "open" && (<>
                      <button className="btn adm-backup-btn adm-report-ok" onClick={() => resolveReport(r)} title={tr("admin.reportResolve")}>
                        <i className="bi bi-check-lg"></i>
                      </button>
                      <button className="btn adm-backup-btn adm-report-del" onClick={() => deleteReport(r)} title={tr("admin.delete")}>
                        <i className="bi bi-trash3"></i>
                      </button>
                    </>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        {reportShot && (
          <div className="adm-modal-overlay" onClick={closeReportShot}>
            <div className="adm-modal adm-report-shot" onClick={e => e.stopPropagation()}>
              <h3>#{reportShot.id} — {tr("admin.reportScreenshot")}</h3>
              <img src={reportShot.url} alt="" />
              <div className="adm-modal-actions">
                <button className="btn" onClick={closeReportShot}>{tr("admin.close")}</button>
              </div>
            </div>
          </div>
        )}

        {reportDetail && (
          <div className="adm-modal-overlay" onClick={() => setReportDetail(null)}>
            <div className="adm-modal adm-report-detail" onClick={e => e.stopPropagation()}>
              <h3>#{reportDetail.id} — {reportDetail.title}</h3>
              <dl>
                <dt>{tr("admin.reportComment")}</dt><dd className="adm-report-comment">{reportDetail.comment || "—"}</dd>
                <dt>{tr("admin.reportMode")}</dt><dd>{reportDetail.mode || "—"}</dd>
                <dt>{tr("admin.reportQuestion")}</dt><dd>{reportDetail.question || "—"}</dd>
                <dt>{tr("admin.reportPhase")}</dt><dd>{reportDetail.phase || "—"}</dd>
                <dt>{tr("admin.reportUser")}</dt>
                <dd>{reportDetail.username
                  ? `${reportDetail.pseudo || reportDetail.username} (${tr("admin.reportAccount", { name: reportDetail.username })}${reportDetail.hasEmail ? "" : ` — ${tr("admin.reportNoEmail")}`})`
                  : `${reportDetail.pseudo || "—"} (${tr("admin.reportGuest")})`}</dd>
                <dt>{tr("admin.reportGame")}</dt><dd>{reportDetail.gameCode || "—"}</dd>
                <dt>{tr("admin.reportDate")}</dt><dd>{fmt(reportDetail.createdAt)}</dd>
                {reportDetail.status !== "open" && (<>
                  <dt>{tr("admin.reportStatus")}</dt>
                  <dd>{STATUS[reportDetail.status] || reportDetail.status} · {fmt(reportDetail.closedAt)}{reportDetail.mailStatus ? ` · ${reportDetail.mailStatus}` : ""}</dd>
                </>)}
              </dl>
              <div className="adm-modal-actions">
                <button className="btn" onClick={() => setReportDetail(null)}>{tr("admin.close")}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Clés d'API ────────────────────────────────────────────────────────────
  if (view === "apikeys") return (
    <div className="adm-layout">
      {sidebar}
      <main className="adm-sys-main">
        <div className="adm-sys-header">
          <h1 className="adm-sys-title"><i className="bi bi-key-fill"></i> {tr("admin.navApiKeys")}</h1>
        </div>
        <MsgBanner />
        <p className="adm-apikey-intro">{tr("admin.apiKeyIntro")}</p>
        <form className="adm-apikey-create" onSubmit={e => { e.preventDefault(); createApiKey(); }}>
          <input className="adm-settings-input" value={apiKeyName} maxLength={60}
            placeholder={tr("admin.apiKeyNamePlaceholder")} onChange={e => setApiKeyName(e.target.value)} />
          <button className="btn btn-primary" type="submit" disabled={!apiKeyName.trim()}>
            <i className="bi bi-plus"></i> {tr("admin.create")}
          </button>
        </form>
        {newApiKey && (
          <div className="adm-apikey-new">
            <p>{tr("admin.apiKeyShownOnce", { name: newApiKey.name })}</p>
            <div className="adm-apikey-value">
              <code>{newApiKey.key}</code>
              <button type="button" className="btn adm-btn-dark adm-backup-btn" title={tr("admin.apiKeyCopy")}
                onClick={() => navigator.clipboard?.writeText(newApiKey.key).then(() => flash(tr("admin.apiKeyCopied")))}>
                <i className="bi bi-clipboard"></i>
              </button>
            </div>
          </div>
        )}
        {apiKeys.length === 0 ? (
          <p className="adm-sys-empty">{tr("admin.apiKeyNone")}</p>
        ) : (
          <div className="adm-backup-list">
            {apiKeys.map(k => (
              <div key={k.id} className="adm-backup-row">
                <i className="bi bi-key-fill adm-backup-icon"></i>
                <div className="adm-backup-info">
                  <span className="adm-backup-name">{k.name} <code>{k.prefix}…</code></span>
                  <span className="adm-backup-meta">
                    {fmt(k.createdAt)} · {k.lastUsedAt ? tr("admin.apiKeyLastUsed", { date: fmt(k.lastUsedAt) }) : tr("admin.apiKeyNeverUsed")}
                  </span>
                </div>
                <div className="adm-backup-actions">
                  <button className="btn adm-btn-danger adm-backup-btn" onClick={() => revokeApiKey(k)} title={tr("admin.apiKeyRevoke")}>
                    <i className="bi bi-trash3"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="adm-apikey-doc">
          <h2>{tr("admin.apiKeyUsage")}</h2>
          <pre>{`GET ${window.location.origin}/api/v1/games
Authorization: Bearer pq_…`}</pre>
          <pre>{`[{
  "code": "K7M2Q",
  "status": "lobby",          // lobby | playing | results
  "mode": { "slug": "blindtest", "name": "Blind Test" },
  "variant": "classique",     // Blind Test uniquement
  "maxPlayers": 8,
  "createdAt": "2026-09-23T20:14:03.000Z",
  "players": [
    { "pseudo": "Novanso", "picture": "https://…/uploads/avatars/….jpg",
      "isHost": true, "online": true, "score": 0 }
  ]
}]`}</pre>
        </div>
      </main>
    </div>
  );

  if (view === "backups") return (
    <div className="adm-layout">
      {sidebar}
      <main className="adm-sys-main">
        <div className="adm-sys-header">
          <h1 className="adm-sys-title"><i className="bi bi-database-fill"></i> {tr("admin.navBackups")}</h1>
          <div className="adm-sys-actions">
            <input type="file" accept=".db" ref={importRef} style={{ display: "none" }}
              onChange={e => { const f = e.target.files[0]; e.target.value = ""; importBackup(f); }} />
            <button className="btn adm-btn-dark" disabled={!!backupBusy} onClick={() => importRef.current?.click()}>
              <i className="bi bi-upload"></i> {tr("admin.backupImport")}
            </button>
            <button className="btn btn-primary" disabled={!!backupBusy} onClick={createBackup}>
              <i className="bi bi-plus"></i> {tr("admin.create")}
            </button>
          </div>
        </div>
        <MsgBanner />
        {backupBusy && (
          <p className="adm-sys-empty"><span className="adm-spinner adm-spinner-inline"></span> {backupBusy}</p>
        )}
        {backups.length === 0 ? (
          <p className="adm-sys-empty">{tr("admin.noBackup")}</p>
        ) : (
          <div className="adm-backup-list">
            {backups.map(b => (
              <div key={b.name} className="adm-backup-row">
                <i className="bi bi-file-earmark-zip-fill adm-backup-icon"></i>
                <div className="adm-backup-info">
                  <span className="adm-backup-name">{b.name}</span>
                  <span className="adm-backup-meta">{fmtSize(b.size)} · {fmt(b.created_at)}</span>
                </div>
                <div className="adm-backup-actions">
                  <button className="btn adm-btn-dark adm-backup-btn" disabled={!!backupBusy}
                    onClick={() => restoreBackup(b.name)} title={tr("admin.backupRestore")}>
                    <i className="bi bi-arrow-counterclockwise"></i>
                  </button>
                  <a className="btn adm-btn-dark adm-backup-btn"
                    href={`${API}/admin/backups/${b.name}/download?token=${token}`}
                    download={b.name} title={tr("admin.download")}>
                    <i className="bi bi-download"></i>
                  </a>
                  <button className="btn adm-btn-danger adm-backup-btn" disabled={!!backupBusy} onClick={() => deleteBackup(b.name)} title={tr("admin.delete")}>
                    <i className="bi bi-trash3"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );

  // ── Logs ──────────────────────────────────────────────────────────────────
  // ── Boutique ──────────────────────────────────────────────────────────────
  if (view === "shop") {
    const RARITY_ORDER = shopAdmin?.rarities || [];
    const list = (shopAdmin?.items || [])
      .filter(i => i.kind === shopKind)
      .sort((a, b) => a.currency.localeCompare(b.currency) || RARITY_ORDER.indexOf(a.rarity) - RARITY_ORDER.indexOf(b.rarity));
    const openNewShopItem = () => setShopModal({
      kind: shopKind, name: "", currency: "coin", rarity: RARITY_ORDER[0] || "commun", image: "",
    });
    const currencyLabel = (c) => c === "junk" ? tr("admin.shopJunk") : tr("admin.shopCoin");
    return (
      <div className="adm-layout">
        {sidebar}
        <main className="adm-sys-main">
          <div className="adm-sys-header">
            <h1 className="adm-sys-title"><i className="bi bi-bag-fill"></i> {tr("admin.navShop")}</h1>
            <button className="btn btn-primary" onClick={openNewShopItem}><i className="bi bi-plus"></i> {tr("admin.add")}</button>
          </div>
          <MsgBanner />
          {!shopAdmin ? <p className="adm-sys-loading">{tr("common.loading")}</p> : (
            <>
              <div className="adm-shop-heading">
                <h2 className="adm-sys-section-title">{tr("admin.shopSections")}</h2>
                <p className="adm-table-secondary">{tr("admin.shopSectionsHint")}</p>
              </div>
              <div className="adm-shop-sections">
                {shopAdmin.sections.map(sec => (
                  <label key={sec.id} className={`adm-shop-section${sec.comingSoon ? " soon" : ""}`}>
                    <span className="adm-table-bold">{sec.label}</span>
                    <span className="adm-shop-section-state">{sec.comingSoon ? tr("admin.shopComingSoon") : ""}</span>
                    <input type="checkbox" checked={sec.comingSoon} onChange={() => toggleShopSection(sec)}
                      aria-label={`${sec.label} — ${tr("admin.shopComingSoon")}`} />
                  </label>
                ))}
              </div>

              <h2 className="adm-sys-section-title">{tr("admin.shopItems")}</h2>
              <div className="adm-filter-pills">
                {shopAdmin.kinds.map(k => (
                  <button key={k} className={`adm-pill${shopKind === k ? " active" : ""}`} onClick={() => setShopKind(k)}>
                    {k === "avatar" ? tr("admin.shopAvatars") : tr("admin.shopBanners")}
                  </button>
                ))}
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead><tr>
                    <th></th><th>{tr("admin.shopTitle")}</th><th>{tr("admin.shopCurrency")}</th>
                    <th>{tr("admin.shopRarity")}</th><th>{tr("admin.shopPrice")}</th><th></th>
                  </tr></thead>
                  <tbody>
                    {list.map(item => (
                      <tr key={item.id}>
                        <td><span className={`adm-shop-preview ${item.kind}`}><ItemPreview item={item} size={40} /></span></td>
                        <td className="adm-table-bold">
                          {item.name}
                          {item.custom && <span className="adm-shop-custom">{tr("admin.shopCustom")}</span>}
                        </td>
                        <td className="adm-table-secondary">{currencyLabel(item.currency)}</td>
                        <td className="adm-table-secondary">{item.rarity}</td>
                        <td className="adm-table-secondary">{item.price}</td>
                        <td className="adm-table-actions">
                          <button className="adm-icon-btn" title={tr("admin.edit")}
                            onClick={() => setShopModal({ id: item.id, kind: item.kind, name: item.name, currency: item.currency, rarity: item.rarity, image: "", preview: item })}>
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button className="adm-icon-btn adm-icon-btn-danger" title={tr("admin.delete")} onClick={() => deleteShopItem(item)}>
                            <i className="bi bi-trash3"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </main>

        {shopModal && (
          <div className="adm-modal-overlay" onClick={() => setShopModal(null)}>
            <div className="adm-modal" onClick={e => e.stopPropagation()}>
              <h3>{shopModal.id ? tr("admin.shopEditItem") : tr("admin.shopNewItem")}</h3>
              {!shopModal.id && (
                <label className="adm-field">
                  <span>{tr("admin.shopKind")}</span>
                  <select className="adm-param-select" value={shopModal.kind} onChange={e => setShopModal({ ...shopModal, kind: e.target.value })}>
                    {shopAdmin.kinds.map(k => <option key={k} value={k}>{k === "avatar" ? tr("admin.shopAvatars") : tr("admin.shopBanners")}</option>)}
                  </select>
                </label>
              )}
              <label className="adm-field">
                <span>{tr("admin.shopTitle")}</span>
                <input className="adm-input" value={shopModal.name} maxLength={60} autoFocus
                  onChange={e => setShopModal({ ...shopModal, name: e.target.value })} />
              </label>
              <label className="adm-field">
                <span>{tr("admin.shopCurrency")}</span>
                <select className="adm-param-select" value={shopModal.currency} onChange={e => setShopModal({ ...shopModal, currency: e.target.value })}>
                  {shopAdmin.currencies.map(c => <option key={c} value={c}>{currencyLabel(c)}</option>)}
                </select>
              </label>
              <label className="adm-field">
                <span>{tr("admin.shopRarity")}</span>
                <select className="adm-param-select" value={shopModal.rarity} onChange={e => setShopModal({ ...shopModal, rarity: e.target.value })}>
                  {shopAdmin.rarities.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <div className="adm-field">
                <span>{tr("admin.shopImage")}</span>
                <div className="adm-shop-image-row">
                  <span className={`adm-shop-preview large ${shopModal.kind}`}>
                    {shopModal.image
                      ? <ItemPreview item={{ kind: shopModal.kind, data: { image: shopModal.image } }} size={72} />
                      : shopModal.preview ? <ItemPreview item={shopModal.preview} size={72} /> : null}
                  </span>
                  <label className="btn adm-btn-dark">
                    {shopUploading ? tr("admin.shopUploading") : tr("admin.shopChooseImage")}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: "none" }}
                      onChange={e => uploadShopImage(e.target.files[0])} />
                  </label>
                </div>
                {shopModal.id && <small className="adm-table-secondary">{tr("admin.shopImageKept")}</small>}
              </div>
              {msg.text && <div className={`adm-msg${msg.error ? " adm-msg-error" : ""}`}>{msg.text}</div>}
              <div className="adm-modal-actions">
                <button className="btn" onClick={() => setShopModal(null)}>{tr("admin.cancel")}</button>
                <button className="btn btn-primary" disabled={shopUploading} onClick={saveShopItem}>
                  {shopModal.id ? tr("admin.save") : tr("admin.create")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view === "logs") return (
    <div className="adm-layout">
      {sidebar}
      <main className="adm-sys-main">
        <div className="adm-sys-header">
          <h1 className="adm-sys-title"><i className="bi bi-terminal-fill"></i> {tr("admin.navLogs")}</h1>
          <button className="btn adm-btn-danger" onClick={clearLogs}><i className="bi bi-trash3"></i> {tr("admin.clear")}</button>
        </div>
        <div className="adm-filter-pills">
          {["all", "info", "warn", "error"].map(l => (
            <button key={l} className={`adm-pill${logFilter === l ? " active" : ""}`} onClick={() => changeLogFilter(l)}>
              {l === "all" ? tr("admin.allFilter") : l.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="adm-log-list adm-log-list-full">
          {logs.length === 0 && <p className="adm-sys-empty">{tr("admin.noLog")}</p>}
          {logs.map(l => (
            <div key={l.id} className={`adm-log-row adm-log-${l.level}`}>
              <span className={`adm-log-badge adm-log-badge-${l.level}`}>{l.level.toUpperCase()}</span>
              <span className="adm-log-action">{l.action}</span>
              {l.detail && <span className="adm-log-detail">{l.detail}</span>}
              <span className="adm-log-time">{fmt(l.created_at)}</span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );

  return null;
}
